import React from 'react';
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigate,
  useOutlet,
  useNavigation,
  useSubmit
} from 'react-router-dom';
import * as Headless from '@headlessui/react';

import { Await } from '@oysterjs/ui/async';
import {
  VoiceAgentOverviewFieldsFragment,
  VoiceAgentFieldsFragment,
  CreateVoiceAgentInput,
  CreateVoiceAgentMutation,
  UpdateVoiceAgentMutation,
  VoiceAgentDirection,
  VoiceAgentConfigurationModelVoice,
  VoiceAgentConfigurationTaskBehaviorBusinessLine,
  VoiceAgentConfigurationTaskBehaviorFnolAutoType,
  VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType,
  VoiceAgentConfigurationTaskBehaviorFnolCommercialPropertyType,
  VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType,
  VoiceAgentConfigurationQuestionsAutoType,
  VoiceAgentConfigurationQuestionsCommercialCommonType,
  VoiceAgentConfigurationQuestionsCommercialLiabilityType,
  VoiceAgentConfigurationQuestionsCommercialPropertyType,
  VoiceAgentConfigurationQuestionsWorkersCompensationType,
  VoiceAgentConfigurationTaskBehaviorServiceAddDriverType,
  VoiceAgentConfigurationTaskBehaviorServiceAddVehicleType,
  VoiceAgentConfigurationTaskBehaviorServiceAddLocationType,
  VoiceAgentTransferBehavior,
  VoiceAgentTransferType,
  VoiceAgentConfigurationQuestionsQualificationType,
  LoggedInAgencyUserFieldsFragment,
  GetVoiceAgentCreateContextQuery,
  SubscriptionEntitlement,
  DayOfWeek,
  VoiceAgentConfigurationTransferTimeOffScheduleInput,
  VoiceAgentTransferDirectoryEntryType,
  VoiceAgentTransferTimeOffScheduleType,
  VoiceAgentSipTransferMode,
  VoiceAgentExternalTransferBehavior,
  Action,
  Resource,
  SubscriptionPlan,
  type ImportSchemaFieldsFragment
} from '~/api/graphql';
import { PermissionButton, PermissionNotice, usePermissions } from '~/components/permissions';
import { canSelfServeSubscription } from '~/components/settings/subscription/self-serve';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { idempotencyKeys } from '~/lib/idempotency';
import {
  addTransferLineOfBusiness,
  countTransferDirectoryEntriesByLineOfBusiness,
  DEFAULT_TRANSFER_LINE_OF_BUSINESS_IDS,
  DEFAULT_TRANSFER_MESSAGE,
  effectiveExternalTransferBehavior,
  effectiveVoiceAgentTransferType,
  ensureLineOfBusinessRequiredContactInfo,
  EXTERNAL_TRANSFER_BEHAVIOR_OPTIONS,
  isVoice35Enabled,
  normalizeTransferLineOfBusinessCatalog,
  removeTransferLineOfBusiness,
  updateTransferLineOfBusiness,
  VOICE35_FALLBACK_ATTEMPT_OPTIONS,
  voice35FallbackAttemptSelectValue,
  voiceAgentFieldsToVoiceAgentInput,
  VoiceAgentSettingsFormInput,
  type VoiceAgentSettingsTransferDirectoryEntryInput,
  type VoiceAgentTransferLineOfBusinessInput
} from '~/components/settings/voice/voice_agent_form_input';
import {
  addressDirectoryErrorsByRow,
  prepareDirectorySave,
  withSubmittedDefaults,
  directoryRowHasError
} from '~/components/settings/voice/voice-transfer-directory-changes';
import { VoiceAccountLookupSection } from '~/components/settings/voice/voice_account_lookup_section';
import {
  subscriptionPlanHasAnswerPolicyQuestion,
  voiceAccountLookupForVoice35,
  DISABLED_VOICE_ACCOUNT_LOOKUP
} from '~/components/settings/voice/voice_account_lookup';
import {
  pendingChangesSaveMessages,
  PendingChangesFormHeader,
  PendingChangesSaveBar,
  PendingChangesSaveToast,
  withPendingChanges
} from '~/components/settings/pending-changes-save';
import { useVoiceAgentPendingChangesSave } from '~/components/settings/voice/use-voice-agent-pending-changes-save';
import { OutboundVoiceAgentSettingsForm } from '~/components/settings/voice/outbound-voice-agent-settings';
import { OutboundVoiceComplianceNotice } from '~/components/settings/voice/outbound-voice-compliance';
import { Spinner } from '@oysterjs/ui/spinner';
import { AnimatePresence } from 'motion/react';
import { Button } from '@oysterjs/ui/button';
import { Badge } from '@oysterjs/ui/badge';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/ui/checkbox';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Textarea } from '@oysterjs/ui/textarea';
import { Select } from '@oysterjs/ui/select';
import { ValidationError, ValidationErrorCode } from '@oysterjs/core/graphql';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { ProLabel } from '~/components/pro-label';
import {
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';
import clsx from 'clsx';
import {
  filterLanguageOptions,
  filterVoiceOptions,
  normalizeLoadedConfiguration,
  voiceAgentLanguageOptions,
  type VoiceAgentLanguageSupportData
} from '~/components/settings/voice/voice_language_support';
import {
  VoiceAgentAgencyInformationSection,
  VoiceAgentBasicInformationSection,
  VoiceAgentCallBehaviorSection,
  VoiceAgentEmailNotificationsSection,
  VoiceAgentVoiceModelSection
} from '~/components/settings/voice/voice-agent-sections';
import { getVoiceAgentAgencyPlaceholders } from '~/components/settings/voice/voice-agent-section-props';
import { VoiceLineOfBusinessCatalogEditor } from '~/components/settings/voice/voice-line-of-business-catalog-editor';
import { transferDirectorySchema } from '~/components/settings/voice/voice-transfer-directory-csv';
import { TransferDirectoryImport } from '~/components/settings/voice/voice_transfer_directory_import';
import { VoiceDirectoryConflictNotice } from '~/components/settings/voice/voice_directory_conflict_notice';
import { TransferDirectorySection } from '~/components/settings/voice/voice_transfer_directory_section';

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

/** Copies legacy label-only directory rows into Name so the structured directory UI is not blank (matches pkg/db sanitize when new cold flow is on). */
function hydrateLegacyTransferDirectoryEntry(
  entry: VoiceAgentSettingsTransferDirectoryEntryInput
): VoiceAgentSettingsTransferDirectoryEntryInput {
  const label = (entry.label ?? '').trim();
  const hasName = Boolean(entry.name && String(entry.name).trim());
  const hasDept = Boolean(entry.department && String(entry.department).trim());
  if (!label || hasName || hasDept) {
    return entry;
  }
  return { ...entry, name: label };
}

import {
  VoiceDiscreteSlider,
  type VoiceSliderOption
} from '~/components/settings/voice/voice-discrete-slider';

const isOutboundVoiceAgent = (agent: VoiceAgentOverviewFieldsFragment) =>
  agent.direction === VoiceAgentDirection.Outbound;

const voiceAgentDirectionLabel = (agent: VoiceAgentOverviewFieldsFragment) =>
  isOutboundVoiceAgent(agent) ? 'Outbound' : 'Inbound';

const voiceAgentSettingsHref = (agent: VoiceAgentOverviewFieldsFragment) =>
  `/app/settings/voice/${agent.id}`;

// Helper function to generate a summary string for a schedule
const getScheduleSummary = (
  schedule: VoiceAgentConfigurationTransferTimeOffScheduleInput
): string => {
  const scheduleType = schedule.type;
  if (!scheduleType) {
    return 'Select a schedule type to configure details';
  }

  if (scheduleType === VoiceAgentTransferTimeOffScheduleType.DailyBreaks) {
    const days = schedule.dailyBreak?.daysOfWeek || [];
    const dayNames: Record<DayOfWeek, string> = {
      [DayOfWeek.Sunday]: 'Sun',
      [DayOfWeek.Monday]: 'Mon',
      [DayOfWeek.Tuesday]: 'Tue',
      [DayOfWeek.Wednesday]: 'Wed',
      [DayOfWeek.Thursday]: 'Thu',
      [DayOfWeek.Friday]: 'Fri',
      [DayOfWeek.Saturday]: 'Sat'
    };
    const dayLabels =
      days.length > 0 ? days.map((d) => dayNames[d]).join(', ') : 'No days selected';
    const startTimeStr = hourMinuteToTimeString(
      schedule.dailyBreak?.startHour,
      schedule.dailyBreak?.startMinute
    );
    const endTimeStr = hourMinuteToTimeString(
      schedule.dailyBreak?.endHour,
      schedule.dailyBreak?.endMinute
    );
    const timeRange =
      startTimeStr && endTimeStr ? `${startTimeStr} - ${endTimeStr}` : 'Time not set';
    return `${dayLabels}: ${timeRange}`;
  } else {
    const date = schedule.scheduledClosure?.date;
    const endDate = schedule.scheduledClosure?.endDate;
    if (date && endDate && date !== endDate) {
      return `${date} - ${endDate}`;
    } else if (date) {
      return date;
    }
    return 'Date not set';
  }
};

// Helper functions to convert between HH:mm string format and hour/minute integers
const timeStringToHourMinute = (timeStr: string): { hour: number; minute: number } | null => {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length !== 2) return null;
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (isNaN(hour) || isNaN(minute)) return null;
  return { hour, minute };
};

const hourMinuteToTimeString = (hour: number | undefined, minute: number | undefined): string => {
  if (hour === undefined || minute === undefined) return '';
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

// Custom discrete slider component
const CREATE_VOICE_AGENT = [{ resource: Resource.VoiceAgent, action: Action.Create }];
const UPDATE_VOICE_AGENT = [{ resource: Resource.VoiceAgent, action: Action.Update }];

export const SettingsVoicePage = () => {
  const outlet = useOutlet();
  const loader = useLoaderData() as {
    data: Promise<{
      voiceAgents: VoiceAgentOverviewFieldsFragment[];
    }>;
  };

  return (
    <>
      <React.Suspense fallback={<VoicePageLoader />}>
        <Await resolve={loader.data}>
          {(data) =>
            data && (
              <div className="space-y-6">
                <VoiceStatusBanner />
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
                        Voice Agents
                      </h3>
                      {!!data?.voiceAgents?.length && (
                        <Button color="sky" href="/app/settings/voice/new">
                          Create Agent
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="px-4 py-5 sm:px-6">
                    {!data.voiceAgents?.length ? (
                      <div className="text-center py-12">
                        <ChatBubbleLeftRightIcon className="mx-auto size-12 text-neutral-400 dark:text-neutral-500" />
                        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                          No voice agents
                        </h3>
                        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                          You haven't added any voice agents yet. Add a new agent to get started.
                        </p>
                        <div className="mt-4">
                          <PermissionButton
                            permissions={CREATE_VOICE_AGENT}
                            color="sky"
                            href="/app/settings/voice/new"
                          >
                            Create Agent
                          </PermissionButton>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {data.voiceAgents.map((agent) => (
                          <Link
                            to={voiceAgentSettingsHref(agent)}
                            key={agent.id}
                            className="flex shrink-0 items-center justify-between hover:cursor-pointer"
                          >
                            <div>
                              <h4 className="m-0 flex items-center gap-2 p-0 font-body text-base font-medium text-neutral-900 dark:text-white">
                                {agent.name}
                                <Badge color="green">{titleCase(agent.state)}</Badge>
                                <Badge color="zinc">{voiceAgentDirectionLabel(agent)}</Badge>
                              </h4>
                              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                                {agent.phoneNumber}
                              </p>
                            </div>
                            <ChevronRightIcon
                              aria-hidden="true"
                              className="size-5 flex-none text-neutral-400 dark:text-neutral-500"
                            />
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          }
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet)}</AnimatePresence>
    </>
  );
};

const VoicePageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading voice AI settings...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

const VoiceStatusBanner = () => {
  return (
    <div className="overflow-hidden rounded-lg bg-gradient-to-r from-sky-700 to-sky-600">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">Get started with voice AI</h3>
          <Button
            color="white"
            href="https://docs.getcara.ai/communication/voiceai"
            target="_blank"
          >
            View Documentation
          </Button>
        </div>
        <p className="mt-2 text-sm text-neutral-200">
          Configure Cara to handle phone calls with customers, create and resolve service requests,
          and return time back to your busy producers.
        </p>
      </div>
    </div>
  );
};

const VoiceAgentLimitReached = (props: {
  numVoiceAgents: number;
  plan: SubscriptionPlan | null | undefined;
  onClose: () => void;
}) => {
  const { hasPermission } = usePermissions();
  const allowance = `${props.numVoiceAgents} voice agent${props.numVoiceAgents === 1 ? '' : 's'}`;

  if (canSelfServeSubscription(hasPermission, props.plan)) {
    return (
      <>
        <DialogTitle>You have reached the maximum number of voice agents</DialogTitle>
        <DialogDescription>
          Your subscription allows {allowance}. To add more, go to Subscription, click Manage Seats,
          and add voice agents under Extra Voice Agents.
        </DialogDescription>
        <DialogActions>
          <Button color="sky" href="/app/settings/subscription">
            Go to Subscription
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogTitle>You have reached the maximum number of voice agents</DialogTitle>
      <DialogDescription>
        {props.plan === SubscriptionPlan.Enterprise
          ? `Your plan includes ${allowance}. To add more, contact your Cara account manager.`
          : `Your subscription allows ${allowance}. Ask an admin on your account to add voice agents to your plan.`}
      </DialogDescription>
      <DialogActions>
        <Button color="white" onClick={props.onClose}>
          Close
        </Button>
      </DialogActions>
    </>
  );
};

export const SettingsVoiceAgentCreatePage = () => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateVoiceAgentMutation>;
  const loader = useLoaderData() as {
    data: Promise<{
      agencyUser: NonNullable<GetVoiceAgentCreateContextQuery['agencyUser']>;
    }>;
  };

  // Reachable by URL, so the dialog checks rather than trusting the button on
  // the list page behind it.
  const canCreate = usePermissions().hasAllPermissions(CREATE_VOICE_AGENT);

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CreateVoiceAgentInput>({ name: '' });
  const [agentType, setAgentType] = React.useState<'inbound' | 'outbound'>('inbound');
  const [step, setStep] = React.useState<'details' | 'compliance'>('details');
  const [complianceAcknowledged, setComplianceAcknowledged] = React.useState(false);
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);
  // Synchronous guard against rapid double-submits before navigation.state updates.
  const submittingRef = React.useRef(false);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
      setStep('details');
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (navigation.state === 'idle') {
      submittingRef.current = false;
    }
  }, [navigation.state]);

  React.useEffect(() => {
    if (agentType === 'inbound') {
      setStep('details');
      setComplianceAcknowledged(false);
    }
  }, [agentType]);

  const submitCreate = (acknowledgmentRequired: boolean, disclosureVersion: string | undefined) => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    const input: CreateVoiceAgentInput = {
      name: form.name || (agentType === 'outbound' ? 'Outbound Voice Agent' : 'Default'),
      direction:
        agentType === 'outbound' ? VoiceAgentDirection.Outbound : VoiceAgentDirection.Inbound
    };
    if (agentType === 'outbound' && acknowledgmentRequired && disclosureVersion) {
      input.outboundComplianceDisclosureVersion = disclosureVersion;
    }
    submit(input, { method: 'POST', encType: 'application/json' });
  };

  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    acknowledgmentRequired: boolean,
    disclosureVersion: string | undefined
  ) => {
    e.preventDefault();
    if (agentType === 'outbound' && acknowledgmentRequired && step === 'details') {
      setStep('compliance');
      setComplianceAcknowledged(false);
      return;
    }
    if (agentType === 'outbound' && acknowledgmentRequired && !complianceAcknowledged) {
      return;
    }
    submitCreate(acknowledgmentRequired, disclosureVersion);
  };

  return (
    <MountableDialog
      onClose={() => navigate(-1)}
      size={step === 'compliance' ? '2xl' : 'md'}
      className="relative"
    >
      <React.Suspense
        fallback={
          <div className="p-12 text-center">
            <Spinner size="size-8" color="neutral" />
          </div>
        }
      >
        <Await resolve={loader?.data}>
          {(data) => {
            if (!data) {
              return null;
            }

            const numVoiceAgents = data.agencyUser.agency?.subscription?.numVoiceAgents || 1;
            const numVoiceAgentsActive =
              data.agencyUser.agency?.subscription?.numVoiceAgentsActive || 0;
            const acknowledgmentRequired =
              data.agencyUser.agency?.outboundVoiceComplianceAcknowledgmentRequired ?? true;
            const disclosure = data.agencyUser.agency?.outboundVoiceComplianceDisclosure;

            if (numVoiceAgentsActive >= numVoiceAgents) {
              return (
                <VoiceAgentLimitReached
                  numVoiceAgents={numVoiceAgents}
                  plan={data.agencyUser.agency?.subscription?.plan}
                  onClose={() => navigate(-1)}
                />
              );
            }

            if (
              (data.agencyUser.agency?.subscription?.numVoiceAgentsActive || 0) <
              (data.agencyUser.agency?.subscription?.numVoiceAgents || 1)
            ) {
              if (step === 'compliance' && disclosure) {
                return (
                  <>
                    <DialogTitle>{disclosure.title}</DialogTitle>
                    <Form
                      onSubmit={(e) => handleSubmit(e, acknowledgmentRequired, disclosure.version)}
                    >
                      <DialogBody>
                        <OutboundVoiceComplianceNotice
                          disclosure={disclosure}
                          acknowledged={complianceAcknowledged}
                          onAcknowledgedChange={setComplianceAcknowledged}
                          disabled={!canCreate || navigation.state === 'submitting'}
                        />
                      </DialogBody>
                      <DialogActions>
                        <Button
                          type="button"
                          color="white"
                          disabled={navigation.state === 'submitting'}
                          onClick={() => setStep('details')}
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          color="sky"
                          loading={navigation.state === 'submitting'}
                          disabled={!canCreate || !complianceAcknowledged}
                        >
                          Acknowledge and Create
                        </Button>
                      </DialogActions>
                    </Form>
                  </>
                );
              }

              return (
                <>
                  <DialogTitle>Create a new voice agent</DialogTitle>
                  <DialogDescription>
                    Create an inbound agent for receiving calls or an outbound agent for
                    notification calls. Each agent gets a dedicated phone number.
                  </DialogDescription>
                  <Form
                    onSubmit={(e) => handleSubmit(e, acknowledgmentRequired, disclosure?.version)}
                  >
                    <DialogBody>
                      {!canCreate && (
                        <PermissionNotice className="mb-6">
                          You don&rsquo;t have permission to create voice agents.
                        </PermissionNotice>
                      )}
                      <Fieldset disabled={!canCreate}>
                        <FieldGroup>
                          <Field>
                            <Label>Agent type</Label>
                            <Description>
                              Inbound agents answer incoming calls. Outbound agents place
                              notification calls to customers.
                            </Description>
                            <Select
                              value={agentType}
                              onChange={(e) =>
                                setAgentType(e.currentTarget.value as 'inbound' | 'outbound')
                              }
                            >
                              <option value="inbound">Inbound: receive calls</option>
                              <option value="outbound">Outbound: place calls</option>
                            </Select>
                          </Field>
                          <Field>
                            <Label>Name</Label>
                            <Description>
                              A brief, descriptive name for the voice agent.
                            </Description>
                            <Input
                              value={form?.name || ''}
                              onChange={(e) => {
                                const name = e.currentTarget.value;
                                updateForm(['name'], (prev) => ({ ...prev, name }));
                              }}
                              invalid={!!getValidationError(validationErrors, ['name'])}
                            />
                            {!!getValidationError(validationErrors, ['name']) && (
                              <ErrorMessage>
                                {getValidationError(validationErrors, ['name'])}
                              </ErrorMessage>
                            )}
                          </Field>
                        </FieldGroup>
                      </Fieldset>
                    </DialogBody>
                    <DialogActions>
                      <Button
                        type="button"
                        color="white"
                        disabled={navigation.state === 'submitting'}
                        onClick={() => navigate(-1)}
                      >
                        Close
                      </Button>
                      <Button
                        type="submit"
                        color="sky"
                        loading={navigation.state === 'submitting'}
                        disabled={!canCreate}
                      >
                        {agentType === 'outbound' && acknowledgmentRequired
                          ? 'Continue'
                          : 'Create Agent'}
                      </Button>
                    </DialogActions>
                  </Form>
                </>
              );
            }
            return null;
          }}
        </Await>
      </React.Suspense>
    </MountableDialog>
  );
};

export const SettingsVoiceAgentPage = () => {
  const outlet = useOutlet();
  const loader = useLoaderData() as {
    data: Promise<{
      voiceAgent: VoiceAgentFieldsFragment;
      agencyUser: LoggedInAgencyUserFieldsFragment;
      voiceAgentLanguageSupport: VoiceAgentLanguageSupportData;
      transferDirectoryImportSchema: ImportSchemaFieldsFragment;
    }>;
  };

  return (
    <>
      <React.Suspense fallback={<VoicePageLoader />}>
        <Await resolve={loader.data}>
          {(data) => {
            if (!data?.voiceAgent || !data?.agencyUser) {
              return null;
            }
            if (data.voiceAgent.direction === VoiceAgentDirection.Outbound) {
              return (
                <OutboundVoiceAgentSettingsForm
                  initialVoiceAgent={data.voiceAgent}
                  agencyUser={data.agencyUser}
                  languageSupport={data.voiceAgentLanguageSupport}
                />
              );
            }
            return (
              <VoiceAgentSettingsForm
                initialVoiceAgent={data.voiceAgent}
                agencyUser={data.agencyUser}
                languageSupport={data.voiceAgentLanguageSupport}
                transferDirectoryImportSchema={data.transferDirectoryImportSchema}
              />
            );
          }}
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet)}</AnimatePresence>
    </>
  );
};

// Persisted rows can predate the current routing rules, so every form snapshot
// loaded from the server is passed through the resolver before it is compared
// against edits. Normalizing both the form and the saved baseline keeps a
// corrected voice from registering as an unsaved change.
const normalizeFormRouting = (
  languageSupport: VoiceAgentLanguageSupportData,
  agent: VoiceAgentFieldsFragment
): VoiceAgentSettingsFormInput => {
  const input = voiceAgentFieldsToVoiceAgentInput(agent);
  const normalized = normalizeLoadedConfiguration(
    languageSupport,
    input.configuration.languages ?? [],
    input.configuration.model.voice ?? VoiceAgentConfigurationModelVoice.Grace
  );
  return {
    ...input,
    configuration: {
      ...input.configuration,
      languages: normalized.languages,
      model: { ...input.configuration.model, voice: normalized.voice }
    }
  };
};

const VoiceAgentSettingsForm = (props: {
  initialVoiceAgent: VoiceAgentFieldsFragment;
  agencyUser: LoggedInAgencyUserFieldsFragment;
  languageSupport: VoiceAgentLanguageSupportData;
  transferDirectoryImportSchema: ImportSchemaFieldsFragment;
}) => {
  const action = useActionData() as ActionResult<UpdateVoiceAgentMutation>;
  const submit = useSubmit();
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_VOICE_AGENT);

  const [expandedSchedules, setExpandedSchedules] = React.useState<Set<number>>(new Set());
  const [openDirectoryEntryIndex, setOpenDirectoryEntryIndex] = React.useState<number | null>(null);

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const conflicts = validationErrors.filter((e) => e.code === ValidationErrorCode.Conflict);
  const conflictNoticeRef = React.useRef<HTMLDivElement>(null);
  // The last save's directory rows, for placing server errors: the page row each change
  // came from, and how many rows the page had.
  const directoryRowOfChange = React.useRef<(number | undefined)[]>([]);
  const directoryRowCountAtSave = React.useRef(0);
  const [form, setForm] = React.useState<VoiceAgentSettingsFormInput>(() =>
    normalizeFormRouting(props.languageSupport, props.initialVoiceAgent)
  );
  const toSavedState = React.useCallback(
    (agent: VoiceAgentFieldsFragment) => normalizeFormRouting(props.languageSupport, agent),
    [props.languageSupport]
  );
  const { updateForm: _updateForm, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  // A save sent again unchanged, as after a lost response, reuses its key.
  const saveKeys = React.useRef(idempotencyKeys());
  const { changesPending, saved, error, discardChanges, markPendingChanges, savedState } =
    useVoiceAgentPendingChangesSave({
      action,
      initialVoiceAgent: props.initialVoiceAgent,
      toSavedState,
      onAfterSave: (savedState) => {
        saveKeys.current.reset();
        setForm(savedState);
        setExpandedSchedules(new Set());
      }
    });

  const updateForm = withPendingChanges(markPendingChanges, _updateForm);

  const hasAdvancedCustomization = props.agencyUser.agency?.entitlements.some(
    (entitlement) => entitlement === SubscriptionEntitlement.AdvancedVoiceCustomization
  );

  const canUseAnswerPolicyQuestion = subscriptionPlanHasAnswerPolicyQuestion(
    props.agencyUser.agency?.subscription?.plan
  );

  const transferType = effectiveVoiceAgentTransferType(form.configuration?.transfer);
  const voice35Enabled = isVoice35Enabled(form.configuration?.transfer);
  // Identity only reads key columns, so the catalog's lines of business don't matter here.
  const directorySchema = React.useMemo(
    () => transferDirectorySchema(props.transferDirectoryImportSchema, []),
    [props.transferDirectoryImportSchema]
  );
  const directoryImportEnabled = useFlag(FEATURE_FLAGS.TRANSFER_DIRECTORY_CSV_IMPORT);
  const showPerIntentTransferBehavior = voice35Enabled;

  // Structured directory (name / department / round-robin tooling) only for cold transfer with the new Retell flow enabled.
  const useStructuredTransferDirectory = React.useMemo(() => {
    const t = form.configuration?.transfer;
    return !!t?.enabled && isVoice35Enabled(t);
  }, [
    form.configuration?.transfer?.enabled,
    form.configuration?.transfer?.useNewColdTransferConversationFlow
  ]);

  // When structured directory is enabled, copy legacy label-only rows into name/department
  // so expanded fields are not blank (matches sanitizeVoiceAgentJSON on read).
  React.useEffect(() => {
    if (!useStructuredTransferDirectory) {
      return;
    }
    setForm((prev) => {
      const directory = prev.configuration?.transfer?.directory;
      if (!directory?.length) {
        return prev;
      }
      let changed = false;
      const next = directory.map((entry) => {
        const hydrated = hydrateLegacyTransferDirectoryEntry(entry);
        if (hydrated.name !== entry.name || hydrated.department !== entry.department) {
          changed = true;
        }
        return hydrated;
      });
      if (!changed) {
        return prev;
      }
      return {
        ...prev,
        configuration: {
          ...prev.configuration,
          transfer: {
            ...prev.configuration?.transfer,
            directory: next
          }
        }
      };
    });
  }, [useStructuredTransferDirectory]);

  // Helper to get current schedules from form
  const getSchedules = (): VoiceAgentConfigurationTransferTimeOffScheduleInput[] => {
    return form.configuration?.transfer?.timeOffSchedules || [];
  };

  // Helper to update schedules in the form
  const updateSchedules = (
    updater: (
      schedules: VoiceAgentConfigurationTransferTimeOffScheduleInput[]
    ) => VoiceAgentConfigurationTransferTimeOffScheduleInput[]
  ) => {
    const currentSchedules = getSchedules();
    const updatedSchedules = updater(currentSchedules);
    updateForm(['configuration', 'transfer', 'timeOffSchedules'], (prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        transfer: {
          ...prev.configuration?.transfer,
          timeOffSchedules: updatedSchedules
        } as typeof prev.configuration.transfer & {
          timeOffSchedules?: VoiceAgentConfigurationTransferTimeOffScheduleInput[];
        }
      }
    }));
  };

  // Helper to update a specific schedule by index
  const updateSchedule = (
    index: number,
    updater: (
      schedule: VoiceAgentConfigurationTransferTimeOffScheduleInput
    ) => VoiceAgentConfigurationTransferTimeOffScheduleInput
  ) => {
    updateSchedules((schedules) => {
      const updated = [...schedules];
      updated[index] = updater(updated[index]);
      return updated;
    });
  };

  // Generate field ID from validation path
  const getFieldId = (path: (string | number)[]): string => {
    return `field-${path.join('-')}`;
  };

  // Scroll to first validation error when errors change
  React.useEffect(() => {
    if (validationErrors.length > 0) {
      // A conflict can't be fixed in a field, so show the notice instead.
      if (validationErrors.some((e) => e.code === ValidationErrorCode.Conflict)) {
        conflictNoticeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const firstError = validationErrors[0];

      // Find the first fieldId that exists on the page, constructed from each prefix of firstError.field
      let element: HTMLElement | null = null;
      for (let i = 0; i < firstError.field.length; i++) {
        const fieldId = getFieldId(firstError.field.slice(0, i + 1));
        element = document.querySelector(`[data-field-id="${fieldId}"]`);
        if (element) {
          break;
        }
      }

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        // Focus the field for better accessibility
        const input = element.querySelector('input, textarea, select, [role="combobox"]');
        if (input instanceof HTMLElement) {
          input.focus();
        }
      }
    }
  }, [JSON.stringify(validationErrors)]);

  React.useEffect(() => {
    setValidationErrors(
      addressDirectoryErrorsByRow(
        action?.validationErrors || [],
        directoryRowOfChange.current,
        directoryRowCountAtSave.current
      )
    );
  }, [action?.validationErrors]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationErrors([]);

    // Filter out schedules without a type before submitting
    const formToSubmit = { ...form };
    let comparableDirectoryRow = (row: VoiceAgentSettingsTransferDirectoryEntryInput) => row;
    const schedulesToSubmit = formToSubmit.configuration?.transfer?.timeOffSchedules;

    if (schedulesToSubmit) {
      // Only include schedules that have a valid type
      const validSchedules = schedulesToSubmit.filter(
        (s) =>
          s.type === VoiceAgentTransferTimeOffScheduleType.DailyBreaks ||
          s.type === VoiceAgentTransferTimeOffScheduleType.ScheduledClosures
      );

      formToSubmit.configuration = {
        ...formToSubmit.configuration,
        transfer: {
          ...formToSubmit.configuration?.transfer,
          timeOffSchedules: validSchedules
        }
      };
    }

    // Ensure transfer config and directory entries include all required mutation fields.
    // Old JSONB records return "" for enum fields, which `??` won't catch; use `||`.
    if (formToSubmit.configuration?.transfer) {
      const directory = formToSubmit.configuration.transfer.directory;
      if (formToSubmit.configuration.transfer.enabled && (directory ?? []).length === 0) {
        setValidationErrors([
          {
            code: ValidationErrorCode.Required,
            field: ['configuration', 'transfer', 'directory'],
            message: 'At least one transfer directory entry is required when transfers are enabled.'
          }
        ]);
        return;
      }

      const resolvedTransferType = effectiveVoiceAgentTransferType(
        formToSubmit.configuration.transfer
      );
      const voice35Submit = isVoice35Enabled(formToSubmit.configuration.transfer);
      const structuredDirectorySubmit =
        !!formToSubmit.configuration.transfer.enabled && voice35Submit;
      if (structuredDirectorySubmit) {
        comparableDirectoryRow = withSubmittedDefaults;
      }

      // Validate structured directory rows only for the new cold-transfer flow.
      if (structuredDirectorySubmit) {
        const directoryErrors: ValidationError[] = [];
        (directory ?? []).forEach((entry, i) => {
          const isLegacyEntry = !entry.name && !entry.department && !!entry.label;
          if (!isLegacyEntry && !entry.name) {
            directoryErrors.push({
              code: ValidationErrorCode.Required,
              field: ['configuration', 'transfer', 'directory', String(i), 'name'],
              message: 'Name is required.'
            });
          }
          if (
            formToSubmit.configuration.transfer.enableExternalTransfer &&
            entry.entryType === VoiceAgentTransferDirectoryEntryType.External &&
            (!entry.callTypes || entry.callTypes.length === 0)
          ) {
            directoryErrors.push({
              code: ValidationErrorCode.Required,
              field: ['configuration', 'transfer', 'directory', String(i), 'callTypes'],
              message: 'At least one call type is required.'
            });
          }
        });
        if (directoryErrors.length > 0) {
          setValidationErrors(directoryErrors);
          return;
        }
      }

      const warmTransfer = resolvedTransferType === VoiceAgentTransferType.Warm;

      formToSubmit.configuration = {
        ...formToSubmit.configuration,
        transfer: {
          ...formToSubmit.configuration.transfer,
          transferType: resolvedTransferType,
          warmTransfer,
          // Old JSONB records (and brand-new fields on existing agents) return "" for enum
          // fields, which `??` won't catch; use `||` so we always submit a valid enum value.
          transferBehaviorQuote:
            formToSubmit.configuration.transfer.transferBehaviorQuote ||
            VoiceAgentTransferBehavior.Immediate,
          transferBehaviorFnol:
            formToSubmit.configuration.transfer.transferBehaviorFnol ||
            VoiceAgentTransferBehavior.AttemptHandling,
          transferBehaviorCoi:
            formToSubmit.configuration.transfer.transferBehaviorCoi ||
            VoiceAgentTransferBehavior.AttemptHandling,
          transferBehaviorService:
            formToSubmit.configuration.transfer.transferBehaviorService ||
            VoiceAgentTransferBehavior.AttemptHandling,
          transferBehaviorCancellation:
            formToSubmit.configuration.transfer.transferBehaviorCancellation ||
            VoiceAgentTransferBehavior.AttemptHandling,
          transferMessage: formToSubmit.configuration.transfer.transferMessage ?? '',
          useNewColdTransferConversationFlow: voice35Submit,
          sipTransferMode:
            resolvedTransferType === VoiceAgentTransferType.Warm
              ? VoiceAgentSipTransferMode.Refer
              : formToSubmit.configuration.transfer.sipTransferMode ||
                VoiceAgentSipTransferMode.Invite,
          directory: structuredDirectorySubmit
            ? (directory ?? []).map(withSubmittedDefaults)
            : directory
        }
      };
    }

    if (formToSubmit.configuration) {
      const voice35Enabled = isVoice35Enabled(formToSubmit.configuration.transfer);
      const directory = formToSubmit.configuration.transfer.directory ?? [];
      formToSubmit.configuration = {
        ...formToSubmit.configuration,
        callBehavior: {
          ...formToSubmit.configuration.callBehavior,
          requiredContactInfo: ensureLineOfBusinessRequiredContactInfo(
            formToSubmit.configuration.callBehavior.requiredContactInfo,
            directory,
            voice35Enabled
          )
        },
        accountLookup: voiceAccountLookupForVoice35(
          formToSubmit.configuration.accountLookup,
          voice35Enabled
        )
      };
    }

    // Sending only changed rows keeps rows others saved meanwhile.
    const submittedDirectory = formToSubmit.configuration?.transfer?.directory ?? [];
    const {
      changes: transferDirectoryChanges,
      rowOfChange,
      clashes
    } = prepareDirectorySave(
      directorySchema,
      savedState.configuration?.transfer?.directory ?? [],
      submittedDirectory,
      comparableDirectoryRow
    );
    if (clashes.length > 0) {
      setValidationErrors(clashes);
      return;
    }
    directoryRowOfChange.current = rowOfChange;
    directoryRowCountAtSave.current = submittedDirectory.length;
    const save = { input: formToSubmit, transferDirectoryChanges };
    submit(
      { ...save, idempotencyKey: saveKeys.current.keyFor(save) },
      { method: 'PUT', encType: 'application/json' }
    );
  };

  const voiceOptions = [
    {
      value: VoiceAgentConfigurationModelVoice.Andrea,
      label: 'Andrea',
      description: 'Mexican Spanish accent, warm'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Charlotte,
      label: 'Charlotte',
      description: 'Southern accent, warm and friendly'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Chloe,
      label: 'Chloe',
      description: 'Clear and professional'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Cimo,
      label: 'Cimo',
      description: 'Modern and versatile'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Eleanor,
      label: 'Eleanor',
      description: 'Midwest accent, approachable'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Evie,
      label: 'Evie',
      description: 'Mandarin accent, calm'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Grace,
      label: 'Grace',
      description: 'Elegant and sophisticated'
    },
    {
      value: VoiceAgentConfigurationModelVoice.HaileyBrazilian,
      label: 'Hailey (Brazilian Portuguese)',
      description: 'Brazilian accent, young and upbeat'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Isabel,
      label: 'Isabel',
      description: 'Spanish accent, warm'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Kate,
      label: 'Kate',
      description: 'Crisp and articulate'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Marissa,
      label: 'Marissa',
      description: 'Smooth and calming'
    },
    {
      value: VoiceAgentConfigurationModelVoice.May,
      label: 'May',
      description: 'Young Chinese voice'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Susan,
      label: 'Susan',
      description: 'Mature and trustworthy'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Zuri,
      label: 'Zuri',
      description: 'Energetic and engaging'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Ashley,
      label: 'Ashley',
      description: 'Warm and conversational'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Crystal,
      label: 'Crystal',
      description: 'Clear and bright'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Hailey,
      label: 'Hailey',
      description: 'Friendly and approachable'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Nia,
      label: 'Nia',
      description: 'Confident and articulate'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Rita,
      label: 'Rita',
      description: 'Professional and polished'
    },
    {
      value: VoiceAgentConfigurationModelVoice.Sophie,
      label: 'Sophie',
      description: 'Gentle and soothing'
    },
    {
      value: VoiceAgentConfigurationModelVoice.MinimaxGrace,
      label: 'Grace',
      description: 'Elegant and refined'
    }
  ];

  const ringDurationOptions: VoiceSliderOption[] = [
    { value: 5000, label: '5s' },
    { value: 10000, label: '10s' },
    { value: 15000, label: '15s' },
    { value: 20000, label: '20s' },
    { value: 25000, label: '25s' },
    { value: 30000, label: '30s' }
  ];

  const selectedLanguages = form.configuration?.languages ?? [];
  const selectedVoice = form.configuration?.model?.voice ?? VoiceAgentConfigurationModelVoice.Grace;

  const filteredVoiceOptions = React.useMemo(
    () => filterVoiceOptions(props.languageSupport, voiceOptions, selectedLanguages, selectedVoice),
    [props.languageSupport, selectedLanguages, selectedVoice]
  );

  const filteredLanguageOptions = React.useMemo(
    () =>
      filterLanguageOptions(
        props.languageSupport,
        voiceAgentLanguageOptions,
        selectedLanguages,
        selectedVoice
      ),
    [props.languageSupport, selectedLanguages, selectedVoice]
  );

  const agencyPlaceholders = React.useMemo(() => {
    const placeholders = getVoiceAgentAgencyPlaceholders(props.agencyUser);
    const { line1, line2, city, zone, postalCode } = props.agencyUser.agency.mailingAddress;

    return {
      ...placeholders,
      mailingAddressPlaceholder: `${line1} ${line2 || ''}\n${city}, ${zone} ${postalCode}`
    };
  }, [props.agencyUser]);

  const sectionProps = {
    form,
    phoneNumber: props.initialVoiceAgent.phoneNumber,
    agencyPlaceholders,
    validationErrors,
    getFieldId,
    getValidationError,
    updateForm,
    filteredVoiceOptions,
    filteredLanguageOptions,
    languageSupport: props.languageSupport,
    hasAdvancedCustomization,
    isOutboundVoice: false,
    supportsMultipleLanguages: true,
    voice35Enabled
  };

  return (
    <Form className="space-y-6" onSubmit={handleSubmit}>
      <VoiceDirectoryConflictNotice ref={conflictNoticeRef} conflicts={conflicts} />
      <PendingChangesSaveToast
        saved={saved}
        error={error}
        messages={pendingChangesSaveMessages.voiceAgent}
      />
      {canUpdate ? (
        <PendingChangesSaveBar
          changesPending={changesPending}
          onDiscard={() => discardChanges(setForm)}
        />
      ) : (
        <PermissionNotice>
          You can see how this voice agent is configured but not change it.
        </PermissionNotice>
      )}
      <PendingChangesFormHeader
        title={`${form.name} Agent Settings`}
        description="Configure the settings for your voice agent."
        backHref="/app/settings/voice"
        changesPending={changesPending}
      />

      <VoiceAgentBasicInformationSection {...sectionProps} />

      <VoiceAgentAgencyInformationSection {...sectionProps} />

      <VoiceAgentCallBehaviorSection {...sectionProps} />

      {voice35Enabled && (
        <VoiceAccountLookupSection
          canUseAnswerPolicyQuestion={canUseAnswerPolicyQuestion}
          accountLookup={form.configuration?.accountLookup ?? DISABLED_VOICE_ACCOUNT_LOOKUP}
          onChange={(accountLookup) => {
            updateForm(['configuration', 'accountLookup'], (prev) => ({
              ...prev,
              configuration: {
                ...prev.configuration,
                accountLookup
              }
            }));
          }}
          getFieldId={getFieldId}
          getValidationError={getValidationError}
          validationErrors={validationErrors}
        />
      )}

      <VoiceAgentVoiceModelSection {...sectionProps} />

      <VoiceAgentEmailNotificationsSection {...sectionProps} />

      {/* Business Lines Configuration */}
      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                Business Lines Configuration
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Configure which business lines Cara should handle calls for.
              </p>
            </div>

            <Fieldset className="flex flex-col gap-y-6 col-span-2">
              <FieldGroup>
                <Field
                  data-field-id={getFieldId(['configuration', 'taskBehavior', 'businessLines'])}
                >
                  <Label>Business Lines</Label>
                  <Description>Which business lines should Cara handle calls for?</Description>
                  <RadioGroup
                    value={form.configuration?.taskBehavior?.businessLines}
                    onChange={(value) => {
                      updateForm(['configuration', 'taskBehavior', 'businessLines'], (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          taskBehavior: {
                            ...prev.configuration?.taskBehavior,
                            businessLines: value as VoiceAgentConfigurationTaskBehaviorBusinessLine
                          }
                        }
                      }));
                    }}
                  >
                    <RadioField>
                      <Radio
                        color="sky"
                        value={VoiceAgentConfigurationTaskBehaviorBusinessLine.All}
                        disabled={
                          form.configuration?.taskBehavior?.businessLines ===
                          VoiceAgentConfigurationTaskBehaviorBusinessLine.Other
                        }
                      />
                      <Label>All Business Lines</Label>
                      <Description>
                        Handle both commercial and personal insurance calls.
                      </Description>
                    </RadioField>
                    <RadioField>
                      <Radio
                        color="sky"
                        value={VoiceAgentConfigurationTaskBehaviorBusinessLine.Commercial}
                        disabled={
                          form.configuration?.taskBehavior?.businessLines ===
                          VoiceAgentConfigurationTaskBehaviorBusinessLine.Other
                        }
                      />
                      <Label>Commercial Only</Label>
                      <Description>
                        Inform callers we only do commercial insurance and will not collect info for
                        other lines of business.
                      </Description>
                    </RadioField>
                    <RadioField>
                      <Radio
                        color="sky"
                        value={VoiceAgentConfigurationTaskBehaviorBusinessLine.Personal}
                        disabled={
                          form.configuration?.taskBehavior?.businessLines ===
                          VoiceAgentConfigurationTaskBehaviorBusinessLine.Other
                        }
                      />
                      <Label>Personal Only</Label>
                      <Description>
                        Inform callers we only do personal insurance and will not collect info for
                        other lines of business.
                      </Description>
                    </RadioField>
                    {form.configuration?.taskBehavior?.businessLines ===
                      VoiceAgentConfigurationTaskBehaviorBusinessLine.Other && (
                      <RadioField>
                        <Radio
                          color="sky"
                          value={VoiceAgentConfigurationTaskBehaviorBusinessLine.Other}
                        />
                        <Label>Other</Label>
                        <Description>Handle other types of business lines.</Description>
                      </RadioField>
                    )}
                  </RadioGroup>
                  {!!getValidationError(validationErrors, [
                    'configuration',
                    'taskBehavior',
                    'businessLines'
                  ]) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, [
                        'configuration',
                        'taskBehavior',
                        'businessLines'
                      ])}
                    </ErrorMessage>
                  )}
                </Field>
              </FieldGroup>
            </Fieldset>
          </div>
        </div>
      </div>

      {/* Quote Requests Configuration */}
      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                Quote Requests
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Configure how Cara handles insurance quote requests and what information she
                collects.
              </p>
              <br />
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                <b>Lines of business:</b> Cara will handle quote requests for all lines of business
                using Cara's innate insurance capabilities. The customizable fields listed here are
                specifically pre-configured to allow quicker edits on common lines of business. If
                you want Cara to ask specific questions for other lines of business then you can
                input the information in the Custom Questions for Specific Lines of Business section
                at the bottom.{' '}
                <i>You DO NOT need to add all Lines of Business for Cara to handle them.</i>
              </p>
            </div>

            <Fieldset className="flex flex-col gap-y-6 col-span-2">
              <FieldGroup>
                {form.configuration?.taskBehavior?.quote?.enabled && (
                  <>
                    {/* Quote Qualification Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'qualification',
                        'questions'
                      ])}
                    >
                      <Label>
                        Qualification Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Questions that should be asked to qualify all callers and capture additional
                        context prior to asking specific quote questions.
                      </Description>
                      <CheckboxGroup>
                        {[
                          {
                            value: VoiceAgentConfigurationQuestionsQualificationType.ReasonForQuote,
                            label:
                              'Reason for Quote (new coverage, prior coverage cancellation, etc.)',
                            description: 'Why the caller is requesting a quote'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsQualificationType.HavePriorPolicy,
                            label: 'Prior Policy and Expiration Date',
                            description: 'Whether the caller has a prior policy'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.quote?.qualification?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'quote',
                                    'qualification',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.quote?.qualification
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          quote: {
                                            ...prev.configuration?.taskBehavior?.quote,
                                            qualification: {
                                              ...prev.configuration?.taskBehavior?.quote
                                                ?.qualification,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Quote Qualification Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'qualification',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Qualification Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions to the qualification questions (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.quote?.qualification?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'quote',
                              'qualification',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  quote: {
                                    ...prev.configuration?.taskBehavior?.quote,
                                    qualification: {
                                      ...prev.configuration?.taskBehavior?.quote?.qualification,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="By when do you need the policy to start?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Commercial Common Quote Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'commercialCommon',
                        'questions'
                      ])}
                    >
                      <Label>
                        Commercial Common Quote Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>Common questions for commercial insurance quotes.</Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialCommonType.BusinessOperations,
                            label: 'Business Operations',
                            description:
                              'Ask about business operations to understand the business type'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialCommonType.BusinessLegalNameAndDba,
                            label: 'Business Legal Name & DBA',
                            description: 'Business legal name and DBA (doing business as) name'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialCommonType.BusinessStartDate,
                            label: 'Business Start Date',
                            description: 'When the business was started or acquired'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialCommonType.EstimatedAnnualRevenue,
                            label: 'Estimated Annual Revenue',
                            description: 'Estimated revenue for the next twelve months'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.quote?.commercialCommon?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'quote',
                                    'commercialCommon',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.quote?.commercialCommon
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          quote: {
                                            ...prev.configuration?.taskBehavior?.quote,
                                            commercialCommon: {
                                              ...prev.configuration?.taskBehavior?.quote
                                                ?.commercialCommon,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Commercial Common Quote Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'commercialCommon',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Commercial Common Quote Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions common to all commercial insurance quotes (one per
                        line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.quote?.commercialCommon?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'quote',
                              'commercialCommon',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  quote: {
                                    ...prev.configuration?.taskBehavior?.quote,
                                    commercialCommon: {
                                      ...prev.configuration?.taskBehavior?.quote?.commercialCommon,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="How many employees do you currently have?&#10;Do you plan to expand your business in the next year?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Auto Quote Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'auto',
                        'questions'
                      ])}
                    >
                      <Label>
                        Auto Insurance Quote Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>Questions for auto insurance quote requests.</Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationQuestionsAutoType.ConfirmBusinessOrPersonalAuto,
                            label: 'Business or Personal Auto',
                            description: 'Confirm if business auto or personal auto quote'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.VinOrVehicleDetails,
                            label: 'VIN or Vehicle Details',
                            description: 'VIN or year, make, model, and trim of each vehicle'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.GaragingAddress,
                            label: 'Garaging Address',
                            description:
                              'Street address, city, state, and zip code where vehicle is kept'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.EstimatedAnnualMileage,
                            label: 'Estimated Annual Mileage',
                            description: 'The estimated annual mileage'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.EstimatedVehicleValue,
                            label: 'Estimated Vehicle Value',
                            description: 'Current estimated value of the vehicle if known'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.VehicleCount,
                            label: 'Vehicle Count',
                            description:
                              'How many vehicles are they looking to insure on this quote?'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.VehicleAcquisitionDate,
                            label: 'Vehicle Acquisition Date',
                            description:
                              'When was the vehicle acquired (purchase date) for EACH vehicle?'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsAutoType.ComprehensiveAndCollisionCoverage,
                            label: 'Comprehensive and Collision Coverage',
                            description:
                              'Would you like comprehensive and collision coverage on this vehicle?'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.DriverCount,
                            label: 'Driver Count',
                            description: 'Count of drivers to be listed on the policy'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.DriverDetails,
                            label: 'Driver Details',
                            description: 'All drivers including name, gender, and birth date'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.DriversLicenseNumbers,
                            label: 'Drivers License Numbers',
                            description: 'License number for each driver if available'
                          },
                          {
                            value: VoiceAgentConfigurationQuestionsAutoType.VehicleUse,
                            label: 'Vehicle Use',
                            description: 'Vehicle use: pleasure, business, or both'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsAutoType.PriorAccidentsOrViolations,
                            label: 'Prior Accidents or Violations',
                            description:
                              'Any accidents or violations in past five years for any driver'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.quote?.auto?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  ['configuration', 'taskBehavior', 'quote', 'auto', 'questions'],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.quote?.auto?.questions ||
                                      [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          quote: {
                                            ...prev.configuration?.taskBehavior?.quote,
                                            auto: {
                                              ...prev.configuration?.taskBehavior?.quote?.auto,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Auto Quote Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'auto',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Auto Quote Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to auto insurance quotes (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.quote?.auto?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            ['configuration', 'taskBehavior', 'quote', 'auto', 'customQuestions'],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  quote: {
                                    ...prev.configuration?.taskBehavior?.quote,
                                    auto: {
                                      ...prev.configuration?.taskBehavior?.quote?.auto,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="Do you have any modifications to your vehicle?&#10;Will this vehicle be used for rideshare or delivery?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Commercial Liability Quote Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'commercialLiability',
                        'questions'
                      ])}
                    >
                      <Label>
                        Commercial Liability Quote Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Questions for commercial liability insurance quotes.
                      </Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialLiabilityType.ImportManufactureOrSellUnderOwnLabel,
                            label: 'Import/Manufacture/Sell Under Own Label',
                            description:
                              'If business sells goods, understand if they import, manufacture, or sell under own label'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialLiabilityType.LocationAddresses,
                            label: 'Location Addresses',
                            description:
                              'Addresses for all locations to be insured with mailing address'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialLiabilityType.RevenueByOperationAndLocation,
                            label: 'Revenue by Operation & Location',
                            description:
                              "Business's estimated revenue from each operation split by location"
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialLiabilityType.CoverageLimits,
                            label: 'Coverage Limits',
                            description: 'Coverage limits if customer knows what they want'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialLiabilityType.UnderwritingQuestions,
                            label: 'Underwriting Questions',
                            description:
                              'Two additional underwriting questions based on business type'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.quote?.commercialLiability?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'quote',
                                    'commercialLiability',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.quote?.commercialLiability
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          quote: {
                                            ...prev.configuration?.taskBehavior?.quote,
                                            commercialLiability: {
                                              ...prev.configuration?.taskBehavior?.quote
                                                ?.commercialLiability,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Commercial Liability Quote Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'commercialLiability',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Commercial Liability Quote Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to commercial liability quotes (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.quote?.commercialLiability?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'quote',
                              'commercialLiability',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  quote: {
                                    ...prev.configuration?.taskBehavior?.quote,
                                    commercialLiability: {
                                      ...prev.configuration?.taskBehavior?.quote
                                        ?.commercialLiability,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="Do you work with any hazardous materials?&#10;What percentage of your work is subcontracted?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Property Quote Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'commercialProperty',
                        'questions'
                      ])}
                    >
                      <Label>
                        Property Quote Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Questions for commercial and personal property insurance quotes.
                      </Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.BuildingAddress,
                            label: 'Building Address',
                            description: 'Address including street, town, state, and zip code'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.OwnershipStatus,
                            label: 'Ownership Status',
                            description: 'If the customer owns or rents the building'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.ResidentialOrPersonalType,
                            label: 'Residential or Personal Type',
                            description: 'If residential/personal like homeowners, condo, or tenant'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.PrimaryOrSecondaryHome,
                            label: 'Primary or Secondary Home',
                            description: 'If this is their primary or secondary home'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.PrimaryHomeAddress,
                            label: 'Primary Home Address',
                            description: 'Address for primary home if this is not the primary home'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.SquareFootage,
                            label: 'Square Footage',
                            description: 'Square footage for each location'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.YearBuiltAndUpdates,
                            label: 'Year Built & Updates',
                            description:
                              'Year built, including updates for roofing, plumbing, electrical, HVAC'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.FramingType,
                            label: 'Framing Type',
                            description: 'Building framing type: wood or metal'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.ExteriorWallType,
                            label: 'Exterior Wall Type',
                            description: 'Masonry (cement/brick) or other (stucco, vinyl, etc.)'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.FireOrBurglarAlarms,
                            label: 'Fire or Burglar Alarms',
                            description:
                              'If alarms are present and if locally sounding or monitored'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.CoverageAmounts,
                            label: 'Coverage Amounts',
                            description:
                              'Building, contents, and tenant betterments coverage amounts'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.SprinklerSystem,
                            label: 'Sprinkler System',
                            description:
                              'For business customers, if they have ceiling sprinkler system'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.PriorLosses,
                            label: 'Prior Losses',
                            description: 'If there are prior losses in the last five years'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsCommercialPropertyType.OtherLocations,
                            label: 'Other Locations',
                            description: 'Any other locations to insure'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.quote?.commercialProperty?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'quote',
                                    'commercialProperty',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.quote?.commercialProperty
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          quote: {
                                            ...prev.configuration?.taskBehavior?.quote,
                                            commercialProperty: {
                                              ...prev.configuration?.taskBehavior?.quote
                                                ?.commercialProperty,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Property Quote Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'commercialProperty',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Property Quote Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to property quotes (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.quote?.commercialProperty?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'quote',
                              'commercialProperty',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  quote: {
                                    ...prev.configuration?.taskBehavior?.quote,
                                    commercialProperty: {
                                      ...prev.configuration?.taskBehavior?.quote
                                        ?.commercialProperty,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="Do you store any flammable materials on the property?&#10;Is there a security system in place?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Workers Compensation Quote Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'workersCompensation',
                        'questions'
                      ])}
                    >
                      <Label>
                        Workers Compensation Quote Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Questions for workers compensation insurance quotes.
                      </Description>
                      <CheckboxGroup>
                        {[
                          {
                            value: VoiceAgentConfigurationQuestionsWorkersCompensationType.Fein,
                            label: 'FEIN',
                            description: 'Federal employer identification number'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsWorkersCompensationType.LocationAddressesWithEmployees,
                            label: 'Location Addresses with Employees',
                            description: 'Address for each location with employees to be covered'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsWorkersCompensationType.EmployeeCountByTypeAndLocation,
                            label: 'Employee Count by Type & Location',
                            description: 'Count of full time and part time W2 workers per location'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsWorkersCompensationType.PayrollByTypeAndLocation,
                            label: 'Payroll by Type & Location',
                            description:
                              'Payroll of full time and part time W2 workers per location'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsWorkersCompensationType.Contractor_1099Payments,
                            label: 'Contractor 1099 Payments',
                            description:
                              'Annual 1099 payments if contractors do not carry workers comp'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsWorkersCompensationType.OwnerOfficerCoverage,
                            label: 'Owner Officer Coverage',
                            description:
                              'Cover owners and officers - collect name and ownership percentage'
                          },
                          {
                            value:
                              VoiceAgentConfigurationQuestionsWorkersCompensationType.UnderwritingQuestions,
                            label: 'Underwriting Questions',
                            description:
                              'Two additional underwriting questions based on business type'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.quote?.workersCompensation?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'quote',
                                    'workersCompensation',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.quote?.workersCompensation
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          quote: {
                                            ...prev.configuration?.taskBehavior?.quote,
                                            workersCompensation: {
                                              ...prev.configuration?.taskBehavior?.quote
                                                ?.workersCompensation,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Workers Compensation Quote Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'quote',
                        'workersCompensation',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Workers Compensation Quote Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to workers compensation quotes (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.quote?.workersCompensation?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'quote',
                              'workersCompensation',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  quote: {
                                    ...prev.configuration?.taskBehavior?.quote,
                                    workersCompensation: {
                                      ...prev.configuration?.taskBehavior?.quote
                                        ?.workersCompensation,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="Are there any seasonal variations in your workforce?&#10;Do you have any remote workers or field employees?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    <Field>
                      <Label>
                        Custom Questions for Specific Lines of Business
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions for specific lines of business. Each entry should
                        specify the line of business and the questions to ask for that line of
                        business.
                      </Description>

                      <div className="space-y-4" data-slot="control">
                        {form.configuration?.taskBehavior?.quote?.custom?.map((entry, index) => (
                          <div
                            key={index}
                            className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 space-y-3"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-neutral-900 dark:text-white">
                                Questions for Line of Business {index + 1}
                              </span>
                              <Button
                                disabled={!hasAdvancedCustomization}
                                color="red"
                                onClick={() => {
                                  const custom = [
                                    ...(form.configuration?.taskBehavior?.quote?.custom || [])
                                  ];
                                  custom.splice(index, 1);
                                  updateForm(
                                    ['configuration', 'taskBehavior', 'quote', 'custom'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          quote: {
                                            ...prev.configuration?.taskBehavior?.quote,
                                            custom
                                          }
                                        }
                                      }
                                    })
                                  );
                                }}
                              >
                                Remove
                              </Button>
                            </div>

                            <Field
                              data-field-id={getFieldId([
                                'configuration',
                                'taskBehavior',
                                'quote',
                                'custom',
                                index.toString(),
                                'lineOfBusiness'
                              ])}
                            >
                              <Label>
                                Line of Business
                                {!hasAdvancedCustomization && <ProLabel />}
                              </Label>
                              <Input
                                disabled={!hasAdvancedCustomization}
                                value={entry.lineOfBusiness || ''}
                                className="max-w-md"
                                onChange={(e) => {
                                  const lineOfBusiness = e.currentTarget.value;
                                  const custom = [
                                    ...(form.configuration?.taskBehavior?.quote?.custom || [])
                                  ];
                                  custom[index] = { ...custom[index], lineOfBusiness };
                                  updateForm(
                                    ['configuration', 'taskBehavior', 'quote', 'custom'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          quote: {
                                            ...prev.configuration?.taskBehavior?.quote,
                                            custom
                                          }
                                        }
                                      }
                                    })
                                  );
                                }}
                                placeholder="e.g., Cyber Liability, Directors & Officers, Umbrella"
                                invalid={
                                  !!getValidationError(validationErrors, [
                                    'configuration',
                                    'taskBehavior',
                                    'quote',
                                    'custom',
                                    index.toString(),
                                    'lineOfBusiness'
                                  ])
                                }
                              />
                              {getValidationError(validationErrors, [
                                'configuration',
                                'taskBehavior',
                                'quote',
                                'custom',
                                index.toString(),
                                'lineOfBusiness'
                              ]) && (
                                <ErrorMessage>
                                  {getValidationError(validationErrors, [
                                    'configuration',
                                    'taskBehavior',
                                    'quote',
                                    'custom',
                                    index.toString(),
                                    'lineOfBusiness'
                                  ])}
                                </ErrorMessage>
                              )}
                            </Field>

                            <Field
                              data-field-id={getFieldId([
                                'configuration',
                                'taskBehavior',
                                'quote',
                                'custom',
                                index.toString(),
                                'questions'
                              ])}
                            >
                              <Label>
                                Questions
                                {!hasAdvancedCustomization && <ProLabel />}
                              </Label>
                              <Textarea
                                disabled={!hasAdvancedCustomization}
                                value={entry.questions?.join('\n') || ''}
                                onChange={(e) => {
                                  const questions = e.currentTarget.value.split('\n');
                                  const custom = [
                                    ...(form.configuration?.taskBehavior?.quote?.custom || [])
                                  ];
                                  custom[index] = { ...custom[index], questions };
                                  updateForm(
                                    ['configuration', 'taskBehavior', 'quote', 'custom'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          quote: {
                                            ...prev.configuration?.taskBehavior?.quote,
                                            custom
                                          }
                                        }
                                      }
                                    })
                                  );
                                }}
                                placeholder="What is your annual revenue?&#10;How many employees do you have?&#10;Do you have any prior claims?"
                                style={{ minHeight: '80px' }}
                                invalid={
                                  !!getValidationError(validationErrors, [
                                    'configuration',
                                    'taskBehavior',
                                    'quote',
                                    'custom',
                                    index.toString(),
                                    'questions'
                                  ])
                                }
                              />
                              {getValidationError(validationErrors, [
                                'configuration',
                                'taskBehavior',
                                'quote',
                                'custom',
                                index.toString(),
                                'questions'
                              ]) && (
                                <ErrorMessage>
                                  {getValidationError(validationErrors, [
                                    'configuration',
                                    'taskBehavior',
                                    'quote',
                                    'custom',
                                    index.toString(),
                                    'questions'
                                  ])}
                                </ErrorMessage>
                              )}
                            </Field>
                          </div>
                        )) || []}

                        <Button
                          disabled={!hasAdvancedCustomization}
                          color="sky"
                          onClick={() => {
                            const custom = [
                              ...(form.configuration?.taskBehavior?.quote?.custom || [])
                            ];
                            custom.push({
                              lineOfBusiness: '',
                              questions: []
                            });
                            updateForm(
                              ['configuration', 'taskBehavior', 'quote', 'custom'],
                              (prev) => ({
                                ...prev,
                                configuration: {
                                  ...prev.configuration,
                                  taskBehavior: {
                                    ...prev.configuration?.taskBehavior,
                                    quote: {
                                      ...prev.configuration?.taskBehavior?.quote,
                                      custom
                                    }
                                  }
                                }
                              })
                            );
                          }}
                        >
                          Add Line of Business Questions
                        </Button>
                      </div>
                    </Field>
                  </>
                )}
              </FieldGroup>
            </Fieldset>
          </div>
        </div>
      </div>

      {/* Service Requests Configuration */}
      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                Service Requests
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Configure how Cara handles policy changes and service requests.
              </p>
              <br />
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                <b>Service Request Types:</b> Cara will handle all service requests using Cara's
                innate insurance capabilities even if not listed here. The customizable fields
                listed here are specifically pre-configured to allow quicker edits on common
                requests. If you want Cara to ask specific questions for requests not listed here,
                then you can input the information in the Other Insurance Instructions section at
                the bottom. <i>You DO NOT need to add all request types for Cara to handle them.</i>
              </p>
            </div>

            <Fieldset className="flex flex-col gap-y-6 col-span-2">
              <FieldGroup>
                {form.configuration?.taskBehavior?.service?.enabled && (
                  <>
                    {/* Add Driver Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'service',
                        'addDriver',
                        'questions'
                      ])}
                    >
                      <Label>
                        Add Driver Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>Questions for adding a new driver to a policy.</Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddDriverType.DriverName,
                            label: 'Driver Name',
                            description: "New driver's first and last name"
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddDriverType.DriverGender,
                            label: 'Driver Gender',
                            description: "New driver's gender"
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddDriverType.DriverDateOfBirth,
                            label: 'Driver Date of Birth',
                            description: "New driver's date of birth"
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddDriverType.DriverLicenseNumber,
                            label: 'Driver License Number',
                            description:
                              "Driver's license number if available (will be repeated back)"
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.service?.addDriver?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'service',
                                    'addDriver',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.service?.addDriver
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          service: {
                                            ...prev.configuration?.taskBehavior?.service,
                                            addDriver: {
                                              ...prev.configuration?.taskBehavior?.service
                                                ?.addDriver,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Add Driver Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'service',
                        'addDriver',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Add Driver Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to adding drivers (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.service?.addDriver?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'service',
                              'addDriver',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  service: {
                                    ...prev.configuration?.taskBehavior?.service,
                                    addDriver: {
                                      ...prev.configuration?.taskBehavior?.service?.addDriver,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="Does the new driver have any previous insurance claims?&#10;Will this driver be the primary operator of any specific vehicle?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Add Vehicle Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'service',
                        'addVehicle',
                        'questions'
                      ])}
                    >
                      <Label>
                        Add Vehicle Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>Questions for adding a new vehicle to a policy.</Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddVehicleType.VinOrVehicleDetails,
                            label: 'VIN or Vehicle Details',
                            description: 'VIN or year, make, model, and trim of the vehicle'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddVehicleType.DesiredCoveragesComprehensiveAndCollision,
                            label: 'Desired Coverages',
                            description: 'Whether comprehensive and collision coverage is desired'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddVehicleType.EstimatedAnnualMileage,
                            label: 'Estimated Annual Mileage',
                            description: 'The estimated annual mileage for this vehicle'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddVehicleType.DriverOrVehicleChanges,
                            label: 'Driver or Vehicle Changes',
                            description:
                              'Any related driver/vehicle changes (new drivers to add, vehicles to remove)'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.service?.addVehicle?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'service',
                                    'addVehicle',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.service?.addVehicle
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          service: {
                                            ...prev.configuration?.taskBehavior?.service,
                                            addVehicle: {
                                              ...prev.configuration?.taskBehavior?.service
                                                ?.addVehicle,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Add Vehicle Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'service',
                        'addVehicle',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Add Vehicle Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to adding vehicles (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.service?.addVehicle?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'service',
                              'addVehicle',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  service: {
                                    ...prev.configuration?.taskBehavior?.service,
                                    addVehicle: {
                                      ...prev.configuration?.taskBehavior?.service?.addVehicle,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="Does the vehicle have any anti-theft devices?&#10;Will this vehicle be used for business purposes?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Add Location Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'service',
                        'addLocation',
                        'questions'
                      ])}
                    >
                      <Label>
                        Add Location Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>Questions for adding a new location to a policy.</Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddLocationType.NewLocationAddress,
                            label: 'New Location Address',
                            description: 'Full street address of the new location'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddLocationType.LocationsToRemove,
                            label: 'Locations to Remove',
                            description: 'Any addresses to be removed, if any'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddLocationType.LocationOwnershipStatus,
                            label: 'Location Ownership Status',
                            description:
                              'Confirm whether the new location is owned or leased/rented'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddLocationType.CoverageLimitsRequested,
                            label: 'Coverage Limits Requested',
                            description: 'Coverage limits requested for this location'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddLocationType.RequestedEffectiveDate,
                            label: 'Requested Effective Date',
                            description: 'Requested effective date for this change'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddLocationType.ClRevenueForLocation,
                            label: 'CL Revenue for Location',
                            description:
                              'For commercial liability policies, associated revenue for this address'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorServiceAddLocationType.WcPayrollAndEmployeeCountForLocation,
                            label: 'WC Payroll & Employee Count',
                            description:
                              'For workers comp policies, payroll and employee count for this location'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.service?.addLocation?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'service',
                                    'addLocation',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.service?.addLocation
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          service: {
                                            ...prev.configuration?.taskBehavior?.service,
                                            addLocation: {
                                              ...prev.configuration?.taskBehavior?.service
                                                ?.addLocation,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Add Location Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'service',
                        'addLocation',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Add Location Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to adding locations (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.service?.addLocation?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'service',
                              'addLocation',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  service: {
                                    ...prev.configuration?.taskBehavior?.service,
                                    addLocation: {
                                      ...prev.configuration?.taskBehavior?.service?.addLocation,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="What type of business operations will occur at this location?&#10;Are there any special security features at this location?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Other Service Instructions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'service',
                        'other'
                      ])}
                    >
                      <Label>
                        Other Service Instructions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Instructions for handling other service requests not covered by standard
                        options.
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={form.configuration?.taskBehavior?.service?.other || ''}
                        onChange={(e) => {
                          const other = e.currentTarget.value;
                          updateForm(
                            ['configuration', 'taskBehavior', 'service', 'other'],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  service: { ...prev.configuration?.taskBehavior?.service, other }
                                }
                              }
                            })
                          );
                        }}
                        invalid={
                          !!getValidationError(validationErrors, [
                            'configuration',
                            'taskBehavior',
                            'service',
                            'other'
                          ])
                        }
                        placeholder="For billing questions, transfer to accounting. For coverage questions, collect policy number first..."
                        style={{ minHeight: '80px' }}
                      />
                      {!!getValidationError(validationErrors, [
                        'configuration',
                        'taskBehavior',
                        'service',
                        'other'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, [
                            'configuration',
                            'taskBehavior',
                            'service',
                            'other'
                          ])}
                        </ErrorMessage>
                      )}
                    </Field>
                  </>
                )}
              </FieldGroup>
            </Fieldset>
          </div>
        </div>
      </div>

      {/* FNOL (First Notice of Loss) Configuration */}
      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                FNOL (First Notice of Loss)
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Configure how Cara handles first notice of loss claims and what information she
                collects.
              </p>
            </div>

            <Fieldset className="flex flex-col gap-y-6 col-span-2">
              <FieldGroup>
                <SwitchField>
                  <Switch
                    color="sky"
                    checked={form.configuration?.taskBehavior?.fnol?.enabled || false}
                    onChange={(enabled) => {
                      updateForm(['configuration', 'taskBehavior', 'fnol', 'enabled'], (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          taskBehavior: {
                            ...prev.configuration?.taskBehavior,
                            fnol: { ...prev.configuration?.taskBehavior?.fnol, enabled }
                          }
                        }
                      }));
                    }}
                  />
                  <Label>Enable FNOL Handling</Label>
                  <Description>
                    Allow Cara to take first notice of loss reports and collect claim information.
                  </Description>
                </SwitchField>

                {form.configuration?.taskBehavior?.fnol?.enabled && (
                  <>
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'customInstructions'
                      ])}
                    >
                      <Label>
                        Custom FNOL Instructions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Custom instructions for handling first notice of loss calls. This will
                        override her default behavior. Only specify this if you would like Cara to
                        do something completely different.
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={form.configuration?.taskBehavior?.fnol?.customInstructions || ''}
                        onChange={(e) => {
                          const customInstructions = e.currentTarget.value;
                          updateForm(
                            ['configuration', 'taskBehavior', 'fnol', 'customInstructions'],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  fnol: {
                                    ...prev.configuration?.taskBehavior?.fnol,
                                    customInstructions
                                  }
                                }
                              }
                            })
                          );
                        }}
                        invalid={
                          !!getValidationError(validationErrors, [
                            'configuration',
                            'taskBehavior',
                            'fnol',
                            'customInstructions'
                          ])
                        }
                        placeholder="Be empathetic when handling claims. Always ask about injuries first..."
                        style={{ minHeight: '80px' }}
                      />
                      {!!getValidationError(validationErrors, [
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'customInstructions'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, [
                            'configuration',
                            'taskBehavior',
                            'fnol',
                            'customInstructions'
                          ])}
                        </ErrorMessage>
                      )}
                    </Field>

                    {/* Auto FNOL Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'auto',
                        'questions'
                      ])}
                    >
                      <Label>
                        Auto Insurance FNOL Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Select which questions Cara should ask for auto insurance claims.
                      </Description>
                      <CheckboxGroup>
                        {[
                          {
                            value: VoiceAgentConfigurationTaskBehaviorFnolAutoType.LossDate,
                            label: 'Loss Date',
                            description: 'Collect the date the loss occurred'
                          },
                          {
                            value: VoiceAgentConfigurationTaskBehaviorFnolAutoType.LossLocation,
                            label: 'Loss Location',
                            description:
                              'Collect the location including street address, town, state, and zip'
                          },
                          {
                            value: VoiceAgentConfigurationTaskBehaviorFnolAutoType.LossDescription,
                            label: 'Loss Description',
                            description:
                              'Take a description of what happened and what caused the loss'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolAutoType.DriverIdentification,
                            label: 'Driver Identification',
                            description:
                              'Identify the drivers involved and get their names and contact info'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolAutoType.InjuryIdentification,
                            label: 'Injury Identification',
                            description: 'Identify if anyone was hurt and collect their information'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolAutoType.VehicleDamageAssessment,
                            label: 'Vehicle Damage Assessment',
                            description: 'Identify damaged vehicles and collect VINs if possible'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolAutoType.VehicleDetailsIfNoVin,
                            label: 'Vehicle Details (Year, Make, Model, No VIN)',
                            description:
                              'Collect make, model, plate number, and year if VIN unavailable'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolAutoType.PoliceReportRequest,
                            label: 'Police Report Request',
                            description: 'Ask for police report number and request copy via email'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.fnol?.auto?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  ['configuration', 'taskBehavior', 'fnol', 'auto', 'questions'],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.fnol?.auto?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          fnol: {
                                            ...prev.configuration?.taskBehavior?.fnol,
                                            auto: {
                                              ...prev.configuration?.taskBehavior?.fnol?.auto,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* FNOL Auto Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'auto',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Auto FNOL Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to auto FNOL claims (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.fnol?.auto?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            ['configuration', 'taskBehavior', 'fnol', 'auto', 'customQuestions'],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  fnol: {
                                    ...prev.configuration?.taskBehavior?.fnol,
                                    auto: {
                                      ...prev.configuration?.taskBehavior?.fnol?.auto,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="What was the weather like at the time of the accident?&#10;Were there any road conditions that contributed to the loss?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Commercial Liability FNOL Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'commercialLiability',
                        'questions'
                      ])}
                    >
                      <Label>
                        Commercial Liability FNOL Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Questions for commercial liability first notice of loss claims.
                      </Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.LossDate,
                            label: 'Loss Date',
                            description: 'Collect the date the loss occurred'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.LossLocation,
                            label: 'Loss Location',
                            description: 'Location including street address, city, state, and zip'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.LossDescription,
                            label: 'Loss Description',
                            description:
                              'Description of what happened and who was injured or damaged'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.InjuredPartiesInfo,
                            label: 'Injured Parties Info',
                            description: 'Name and contact information of injured parties'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.PremiseLiabilityOwnership,
                            label: 'Premise Liability Ownership',
                            description:
                              'Determine if premise liability and confirm ownership/tenant status'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.ProductLiabilityProducts,
                            label: 'Product Liability Products',
                            description:
                              'For product liability, collect products involved in incident'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.ProductLiabilityRole,
                            label: 'Product Liability Role',
                            description: 'Confirm if manufacturer, importer, brand owner, or vendor'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.OtherLiabilityMaterialOrAction,
                            label: 'Other Liability Material/Action',
                            description: 'Confirm what material or action from insured is involved'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.WitnessesInfo,
                            label: 'Witnesses Info',
                            description: 'Name and contact info for any witnesses'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialLiabilityType.PoliceReportNumber,
                            label: 'Police Report Number',
                            description: 'Collect police report number if available'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.fnol?.commercialLiability?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'fnol',
                                    'commercialLiability',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.fnol?.commercialLiability
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          fnol: {
                                            ...prev.configuration?.taskBehavior?.fnol,
                                            commercialLiability: {
                                              ...prev.configuration?.taskBehavior?.fnol
                                                ?.commercialLiability,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Commercial Liability FNOL Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'commercialLiability',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Commercial Liability FNOL Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to commercial liability FNOL claims (one per
                        line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.fnol?.commercialLiability?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'fnol',
                              'commercialLiability',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  fnol: {
                                    ...prev.configuration?.taskBehavior?.fnol,
                                    commercialLiability: {
                                      ...prev.configuration?.taskBehavior?.fnol
                                        ?.commercialLiability,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="Do you need additional insured certificates?&#10;What is the nature of your business relationship with the injured party?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Property FNOL Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'commercialProperty',
                        'questions'
                      ])}
                    >
                      <Label>
                        Property FNOL Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>Questions for property first notice of loss claims.</Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialPropertyType.LossDate,
                            label: 'Loss Date',
                            description: 'Collect the date the loss occurred'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialPropertyType.LossLocation,
                            label: 'Loss Location',
                            description: 'Location including street address, town, state, and zip'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialPropertyType.LossDescriptionAndCause,
                            label: 'Loss Description & Cause',
                            description:
                              'Description and cause (fire, theft, lightning, hail, water, wind)'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialPropertyType.PropertyUsability,
                            label: 'Property Usability',
                            description:
                              'Confirm if property is still usable or needs temporary relocation'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolCommercialPropertyType.PoliceReportRequest,
                            label: 'Police Report Request',
                            description: 'Ask for police report number and request copy via email'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.fnol?.commercialProperty?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'fnol',
                                    'commercialProperty',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.fnol?.commercialProperty
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          fnol: {
                                            ...prev.configuration?.taskBehavior?.fnol,
                                            commercialProperty: {
                                              ...prev.configuration?.taskBehavior?.fnol
                                                ?.commercialProperty,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Property FNOL Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'commercialProperty',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Property FNOL Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to property FNOL claims (one per line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.fnol?.commercialProperty?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'fnol',
                              'commercialProperty',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  fnol: {
                                    ...prev.configuration?.taskBehavior?.fnol,
                                    commercialProperty: {
                                      ...prev.configuration?.taskBehavior?.fnol?.commercialProperty,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="Are there any security cameras that might have recorded the incident?&#10;How long has the property been vacant, if applicable?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>

                    {/* Workers Compensation FNOL Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'workersCompensation',
                        'questions'
                      ])}
                    >
                      <Label>
                        Workers Compensation FNOL Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Questions for workers compensation first notice of loss claims.
                      </Description>
                      <CheckboxGroup>
                        {[
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.ConfirmFein,
                            label: 'Confirm FEIN',
                            description:
                              'Confirm employer federal identification number (business tax ID)'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.OshaCaseNumber,
                            label: 'OSHA Case Number',
                            description:
                              'Collect OSHA case number or injury reporting number if available'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.InjuryDescription,
                            label: 'Injury Description',
                            description:
                              'Description of how injury occurred with clarifying questions'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.EmployeeName,
                            label: 'Employee Name',
                            description: 'Employee first and last name'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.EmployeeDateOfBirth,
                            label: 'Employee Date of Birth',
                            description: 'Employee date of birth'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.EmployeeWeeklyWages,
                            label: 'Employee Weekly Wages',
                            description: 'Average weekly wages for the employee'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.EmployeeJobTitle,
                            label: 'Employee Job Title',
                            description: 'Employee job title or occupation in the business'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.EmployeeHireDateAndStatus,
                            label: 'Employee Hire Date & Status',
                            description: 'Date of hire and if they are still employed'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.EmployeeContactInfo,
                            label: 'Employee Contact Info',
                            description: 'Employee address, phone, and email if available'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.EmployeeMaritalStatus,
                            label: 'Employee Marital Status',
                            description: 'Employee marital status if available'
                          },
                          {
                            value:
                              VoiceAgentConfigurationTaskBehaviorFnolWorkersCompensationType.EmployeeDependents,
                            label: 'Employee Dependents',
                            description: 'Employee number of dependents if available'
                          }
                        ].map((option) => (
                          <CheckboxField key={option.value}>
                            <Checkbox
                              color="sky"
                              disabled={!hasAdvancedCustomization}
                              checked={
                                form.configuration?.taskBehavior?.fnol?.workersCompensation?.questions?.includes(
                                  option.value
                                ) || false
                              }
                              onChange={(checked) => {
                                updateForm(
                                  [
                                    'configuration',
                                    'taskBehavior',
                                    'fnol',
                                    'workersCompensation',
                                    'questions'
                                  ],
                                  (prev) => {
                                    const current =
                                      prev.configuration?.taskBehavior?.fnol?.workersCompensation
                                        ?.questions || [];
                                    const updated = checked
                                      ? [
                                          ...current.filter((item) => item !== option.value),
                                          option.value
                                        ]
                                      : current.filter((item) => item !== option.value);
                                    return {
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        taskBehavior: {
                                          ...prev.configuration?.taskBehavior,
                                          fnol: {
                                            ...prev.configuration?.taskBehavior?.fnol,
                                            workersCompensation: {
                                              ...prev.configuration?.taskBehavior?.fnol
                                                ?.workersCompensation,
                                              questions: updated
                                            }
                                          }
                                        }
                                      }
                                    };
                                  }
                                );
                              }}
                            />
                            <Label>{option.label}</Label>
                          </CheckboxField>
                        ))}
                      </CheckboxGroup>
                    </Field>

                    {/* Workers Compensation FNOL Custom Questions */}
                    <Field
                      data-field-id={getFieldId([
                        'configuration',
                        'taskBehavior',
                        'fnol',
                        'workersCompensation',
                        'customQuestions'
                      ])}
                    >
                      <Label>
                        Workers Compensation FNOL Custom Questions
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Add custom questions specific to workers compensation FNOL claims (one per
                        line).
                      </Description>
                      <Textarea
                        disabled={!hasAdvancedCustomization}
                        value={
                          form.configuration?.taskBehavior?.fnol?.workersCompensation?.customQuestions?.join(
                            '\n'
                          ) || ''
                        }
                        onChange={(e) => {
                          const customQuestions = e.currentTarget.value.split('\n');
                          updateForm(
                            [
                              'configuration',
                              'taskBehavior',
                              'fnol',
                              'workersCompensation',
                              'customQuestions'
                            ],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                taskBehavior: {
                                  ...prev.configuration?.taskBehavior,
                                  fnol: {
                                    ...prev.configuration?.taskBehavior?.fnol,
                                    workersCompensation: {
                                      ...prev.configuration?.taskBehavior?.fnol
                                        ?.workersCompensation,
                                      customQuestions
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="What personal protective equipment was being used?&#10;Have there been any similar incidents at this location previously?"
                        style={{ minHeight: '80px' }}
                      />
                    </Field>
                  </>
                )}
              </FieldGroup>
            </Fieldset>
          </div>
        </div>
      </div>

      {/* Proof of insurance requests configuration */}
      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                Proof of insurance requests
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Configure how Cara handles COI, EOI, and ID card requests.
              </p>
            </div>

            <Fieldset className="flex flex-col gap-y-6 col-span-2">
              <FieldGroup>
                <SwitchField>
                  <Switch
                    color="sky"
                    checked={form.configuration?.taskBehavior?.coi?.enabled || false}
                    onChange={(enabled) => {
                      updateForm(['configuration', 'taskBehavior', 'coi', 'enabled'], (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          taskBehavior: {
                            ...prev.configuration?.taskBehavior,
                            coi: { ...prev.configuration?.taskBehavior?.coi, enabled }
                          }
                        }
                      }));
                    }}
                  />
                  <Label>Enable proof of insurance requests</Label>
                  <Description>Allow Cara to handle COI, EOI, and ID card requests.</Description>
                </SwitchField>

                {form.configuration?.taskBehavior?.coi?.enabled && (
                  <Field
                    data-field-id={getFieldId([
                      'configuration',
                      'taskBehavior',
                      'coi',
                      'customInstructions'
                    ])}
                  >
                    <Label>
                      Custom proof of insurance instructions
                      {!hasAdvancedCustomization && <ProLabel />}
                    </Label>
                    <Description>
                      Custom instructions for COI, EOI, and ID card requests. This will override her
                      default behavior. Only specify this if you would like Cara to do something
                      completely different.
                    </Description>
                    <Textarea
                      disabled={!hasAdvancedCustomization}
                      value={form.configuration?.taskBehavior?.coi?.customInstructions || ''}
                      onChange={(e) => {
                        const customInstructions = e.currentTarget.value;
                        updateForm(
                          ['configuration', 'taskBehavior', 'coi', 'customInstructions'],
                          (prev) => ({
                            ...prev,
                            configuration: {
                              ...prev.configuration,
                              taskBehavior: {
                                ...prev.configuration?.taskBehavior,
                                coi: {
                                  ...prev.configuration?.taskBehavior?.coi,
                                  customInstructions
                                }
                              }
                            }
                          })
                        );
                      }}
                      invalid={
                        !!getValidationError(validationErrors, [
                          'configuration',
                          'taskBehavior',
                          'coi',
                          'customInstructions'
                        ])
                      }
                      placeholder="Always verify the certificate holder information and purpose..."
                      style={{ minHeight: '80px' }}
                    />
                    {!!getValidationError(validationErrors, [
                      'configuration',
                      'taskBehavior',
                      'coi',
                      'customInstructions'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'configuration',
                          'taskBehavior',
                          'coi',
                          'customInstructions'
                        ])}
                      </ErrorMessage>
                    )}
                    {!!form.configuration?.taskBehavior?.coi?.customInstructions && (
                      <div
                        data-slot="error"
                        className="rounded-md bg-yellow-50 p-4 dark:bg-yellow-500/10 dark:outline dark:outline-yellow-500/15"
                      >
                        <div className="flex">
                          <div className="shrink-0">
                            <ExclamationTriangleIcon
                              aria-hidden="true"
                              className="size-5 text-amber-800 dark:text-amber-100"
                            />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-amber-800 dark:text-amber-100 m-0 p-0">
                              This will override the default proof of insurance behavior.
                            </h3>
                            <p className="mt-2 text-sm text-amber-700 dark:text-amber-100/80">
                              Specifying custom instructions will override any existing instructions
                              to Cara on how to handle proof of insurance requests. Your
                              instructions will be followed and no other behavior will be added to
                              Cara.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </Field>
                )}
              </FieldGroup>
            </Fieldset>
          </div>
        </div>
      </div>

      {/* LPR (Lost Policy Release) Configuration */}
      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                LPR Requests
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Configure how Cara handles policy cancellation requests.
              </p>
            </div>

            <Fieldset className="flex flex-col gap-y-6 col-span-2">
              <FieldGroup>
                <SwitchField>
                  <Switch
                    color="sky"
                    checked={form.configuration?.taskBehavior?.lpr?.enabled || false}
                    onChange={(enabled) => {
                      updateForm(['configuration', 'taskBehavior', 'lpr', 'enabled'], (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          taskBehavior: {
                            ...prev.configuration?.taskBehavior,
                            lpr: { ...prev.configuration?.taskBehavior?.lpr, enabled }
                          }
                        }
                      }));
                    }}
                  />
                  <Label>Enable LPR Collection</Label>
                  <Description>
                    Allow Cara to handle Lost Policy Release information collection.
                  </Description>
                </SwitchField>

                {form.configuration?.taskBehavior?.lpr?.enabled && (
                  <Field
                    data-field-id={getFieldId([
                      'configuration',
                      'taskBehavior',
                      'lpr',
                      'customInstructions'
                    ])}
                  >
                    <Label>
                      Custom LPR Instructions
                      {!hasAdvancedCustomization && <ProLabel />}
                    </Label>
                    <Description>
                      Custom instructions for handling Lost Policy Release calls. This will override
                      her default behavior. Only specify this if you would like Cara to do something
                      completely different.
                    </Description>
                    <Textarea
                      disabled={!hasAdvancedCustomization}
                      value={form.configuration?.taskBehavior?.lpr?.customInstructions || ''}
                      onChange={(e) => {
                        const customInstructions = e.currentTarget.value;
                        updateForm(
                          ['configuration', 'taskBehavior', 'lpr', 'customInstructions'],
                          (prev) => ({
                            ...prev,
                            configuration: {
                              ...prev.configuration,
                              taskBehavior: {
                                ...prev.configuration?.taskBehavior,
                                lpr: {
                                  ...prev.configuration?.taskBehavior?.lpr,
                                  customInstructions
                                }
                              }
                            }
                          })
                        );
                      }}
                      invalid={
                        !!getValidationError(validationErrors, [
                          'configuration',
                          'taskBehavior',
                          'lpr',
                          'customInstructions'
                        ])
                      }
                      placeholder="Focus on safety measures and risk mitigation strategies..."
                      style={{ minHeight: '80px' }}
                    />
                    {!!getValidationError(validationErrors, [
                      'configuration',
                      'taskBehavior',
                      'lpr',
                      'customInstructions'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'configuration',
                          'taskBehavior',
                          'lpr',
                          'customInstructions'
                        ])}
                      </ErrorMessage>
                    )}
                    {!!form.configuration?.taskBehavior?.lpr?.customInstructions && (
                      <div
                        data-slot="error"
                        className="rounded-md bg-yellow-50 p-4 dark:bg-yellow-500/10 dark:outline dark:outline-yellow-500/15"
                      >
                        <div className="flex">
                          <div className="shrink-0">
                            <ExclamationTriangleIcon
                              aria-hidden="true"
                              className="size-5 text-amber-800 dark:text-amber-100"
                            />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-amber-800 dark:text-amber-100 m-0 p-0">
                              This will override the default LPR behavior.
                            </h3>
                            <p className="mt-2 text-sm text-amber-700 dark:text-amber-100/80">
                              Specifying custom instructions will override any existing instructions
                              to Cara on how to handle LPR requests. Your instructions will be
                              followed and no other behavior will be added to Cara.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </Field>
                )}
              </FieldGroup>
            </Fieldset>
          </div>
        </div>
      </div>

      {/* Transfer Settings */}
      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                Transfer Settings
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Configure when and how Cara should transfer calls to human agents.
              </p>
              <br />
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Cara will transfer in three scenarios during business hours pending any setting
                adjustments made here:
              </p>
              <ol className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                <li>
                  {' '}
                  <b>1. Specifically Asked:</b> If the caller specifically asks for a named agent,
                  any agent, or a human then Cara will attempt to transfer.
                </li>
                <li>
                  {' '}
                  <b>2. Customer Frustration:</b> If the caller sounds frustrated then Cara will
                  attempt to transfer.
                </li>
                <li>
                  {' '}
                  <b>3. Payment Requested:</b> If the caller is looking to make a payment then Cara
                  will attempt to transfer since Cara cannot collect payment details for client
                  privacy.
                </li>
              </ol>
              <p className="mt-4 text-sm/6 text-neutral-600 dark:text-neutral-400">
                When Voice 3.5 is enabled, you can also choose immediate transfer vs.
                attempt-handling-first separately for quotes, claims, COI, EOI, and ID card
                requests, cancellation, and servicing.
              </p>
              <Headless.Disclosure as="div" className="mt-6">
                {({ open }) => (
                  <>
                    <Headless.DisclosureButton className="flex w-full items-center gap-2 text-left group">
                      <ChevronDownIcon
                        className={clsx(
                          'h-4 w-4 shrink-0 text-neutral-400 dark:text-neutral-500 transition-transform duration-200',
                          open ? 'rotate-180' : ''
                        )}
                      />
                      <span className="font-body font-semibold text-neutral-900 dark:text-white">
                        Transfer routing
                      </span>
                    </Headless.DisclosureButton>
                    <Headless.DisclosurePanel className="mt-2 space-y-2">
                      <p className="text-sm/6 text-neutral-600 dark:text-neutral-400">
                        With Voice 3.5, when several <b>internal</b> directory entries are equally
                        good matches for a call, Cara spreads those calls across them instead of
                        always picking the same person. Rotation is fair and persistent: per voice
                        agent, Cara routes to whoever was used least recently, and entries that have
                        never been used go first. It is a tracked rotation, not random selection.
                        These rules apply to the internal directory only — external entries use
                        strict name matching instead (see External transfers).
                      </p>
                      <ul className="text-sm/6 text-neutral-600 dark:text-neutral-400 list-disc pl-5">
                        <li>
                          <b>When it applies:</b> only when two or more entries tie as the best
                          match — for example, the caller gives no name and several entries qualify
                          for the call type.
                        </li>
                        <li>
                          <b>A specific match always wins outright</b> (no rotation), in this order:{' '}
                          <b>Servicing code</b>, then <b>Alternate names</b>, then <b>Name</b>. Cara
                          still records the pick so the relevant rotation stays fair next time.
                        </li>
                        <li>
                          <b>Same name, different people:</b> if the caller names a specific person
                          but several distinct people match (e.g. two entries named
                          &quot;Joe&quot;), Cara asks the caller to clarify rather than rotating
                          between them.
                        </li>
                        <li>
                          <b>Call Types</b> (per internal directory entry): limit which entries are
                          eligible for which call types. Leave empty on an internal entry to make it
                          eligible for every call type. External entries require at least one call
                          type (see External transfers).
                        </li>
                        <li>
                          <b>Call type routing:</b> if only the call type is known (e.g. Claims) and
                          no person is named, Cara narrows to entries tagged for Claims (plus
                          untagged entries), then rotates among them.
                        </li>
                        <li>
                          <b>Team/department routing</b> is temporarily unavailable in the settings
                          UI; call-type and name-based routing still apply.
                        </li>
                        <li>
                          <b>No good match:</b> if nothing scores well enough, Cara keeps handling
                          the call itself instead of transferring.
                        </li>
                      </ul>
                    </Headless.DisclosurePanel>
                  </>
                )}
              </Headless.Disclosure>
              <Headless.Disclosure as="div" className="mt-6">
                {({ open }) => (
                  <>
                    <Headless.DisclosureButton className="flex w-full items-center gap-2 text-left group">
                      <ChevronDownIcon
                        className={clsx(
                          'h-4 w-4 shrink-0 text-neutral-400 dark:text-neutral-500 transition-transform duration-200',
                          open ? 'rotate-180' : ''
                        )}
                      />
                      <span className="font-body font-semibold text-neutral-900 dark:text-white">
                        External transfers
                      </span>
                    </Headless.DisclosureButton>
                    <Headless.DisclosurePanel className="mt-2 space-y-2">
                      <p className="text-sm/6 text-neutral-600 dark:text-neutral-400">
                        Voice 3.5 can route a caller either inside the agency or to an outside
                        party. Internal and external transfers live in separate directory sections.
                        Cara decides internal vs. external from the conversation based on your{' '}
                        <b>External transfer behavior</b> setting (see below). Carrier names from
                        your AMS (via lookup_customer) are the primary signal for external matching.
                      </p>
                      <ul className="text-sm/6 text-neutral-600 dark:text-neutral-400 list-disc pl-5">
                        <li>
                          <b>Internal Transfer Directory (default):</b> agency staff. Uses normal
                          transfer routing, round-robin, and <b>Fallback Attempts</b>.
                        </li>
                        <li>
                          <b>External Transfer Directory:</b> outside parties such as carrier claim
                          centers, lenders, and specialist vendors. This section appears only when
                          Voice 3.5 and <b>Enable External Transfer</b> are both on.
                        </li>
                        <li>
                          <b>Strict external matching:</b> Cara transfers externally only when a
                          carrier or vendor name from your AMS (or the caller) matches a single
                          external directory entry with high confidence. She will not transfer on
                          fuzzy guesses, ambiguous ties, or without a carrier signal.
                        </li>
                        <li>
                          <b>No external fallbacks:</b> Cara dials at most <b>one</b> external
                          number per call. She will not try a second external entry if the first
                          does not answer. <b>Fallback Attempts</b> controls how many internal
                          numbers she may dial on the same call when retrying after a failed
                          transfer.
                        </li>
                        <li>
                          <b>External routing requires Enable External Transfer</b> and a matching
                          external directory entry. Each external entry must have at least one call
                          type selected to enable external transfers for those types.
                        </li>
                        <li>
                          <b>External transfer behavior:</b> <b>Best match</b> scores internal and
                          external entries together;
                          <b> External number first, internal fallback allowed</b> tries external
                          routing first when Cara selects external; <b>External numbers only</b>{' '}
                          never falls back to internal. Internal fallback after a failed or
                          unanswered external transfer requires at least <b>One Fallback Call</b>{' '}
                          under Fallback Attempts.
                        </li>
                        <li>
                          <b>The same call type can live on both sides:</b> tagging Claims on an
                          internal entry makes that person eligible for internal claims calls;
                          tagging Claims on an external entry lets claims calls route externally
                          when Cara has a matching party name (often from your AMS via
                          lookup_customer).
                        </li>
                      </ul>
                      <p className="text-sm/6 text-neutral-600 dark:text-neutral-400">
                        Per-entry <b>Call Types</b> on internal and external directory rows decide
                        which numbers are eligible for each call type. Internal entries with no call
                        types selected are eligible for all types; external entries require at least
                        one. External entries are matched by name (and alternate names), not
                        round-robin rotation.
                      </p>
                    </Headless.DisclosurePanel>
                  </>
                )}
              </Headless.Disclosure>
            </div>

            <Fieldset className="flex min-w-0 flex-col gap-y-6 col-span-2">
              <FieldGroup className="min-w-0">
                <SwitchField>
                  <Switch
                    color="sky"
                    checked={form.configuration?.transfer?.enabled || false}
                    onChange={(enabled) => {
                      updateForm(['configuration', 'transfer', 'enabled'], (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          transfer: { ...prev.configuration?.transfer, enabled }
                        }
                      }));
                    }}
                  />
                  <Label>Enable Transfers</Label>
                  <Description>Allow Cara to transfer calls to other phone numbers.</Description>
                </SwitchField>

                <SwitchField>
                  <Switch
                    color="sky"
                    checked={
                      form.configuration?.transfer?.useNewColdTransferConversationFlow || false
                    }
                    onChange={(useNewColdTransferConversationFlow) => {
                      updateForm(['configuration', 'transfer'], (prev) => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          transfer: {
                            ...prev.configuration?.transfer,
                            useNewColdTransferConversationFlow,
                            ...(useNewColdTransferConversationFlow
                              ? {
                                  lineOfBusinessCatalog: normalizeTransferLineOfBusinessCatalog(
                                    prev.configuration?.transfer.lineOfBusinessCatalog
                                  )
                                }
                              : {}),
                            ...(!useNewColdTransferConversationFlow
                              ? {
                                  transferType: effectiveVoiceAgentTransferType({
                                    ...prev.configuration?.transfer,
                                    useNewColdTransferConversationFlow: false
                                  })
                                }
                              : {})
                          }
                        }
                      }));
                    }}
                  />
                  <Label>Use Voice 3.5</Label>
                  <Description>
                    Uses the latest Voice 3.5 conversation flow and settings. Turn off to use the
                    legacy flow and simpler transfer configuration.
                  </Description>
                </SwitchField>

                {form.configuration?.transfer?.enabled && (
                  <>
                    <Field data-field-id={getFieldId(['configuration', 'transfer', 'behavior'])}>
                      <Label>
                        Transfer Behavior
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>How should Cara handle transfer requests?</Description>
                      <RadioGroup
                        disabled={!hasAdvancedCustomization}
                        value={
                          hasAdvancedCustomization
                            ? form.configuration?.transfer?.behavior
                            : VoiceAgentTransferBehavior.Immediate
                        }
                        onChange={(value) => {
                          updateForm(['configuration', 'transfer', 'behavior'], (prev) => ({
                            ...prev,
                            configuration: {
                              ...prev.configuration,
                              transfer: {
                                ...prev.configuration?.transfer,
                                behavior: value as VoiceAgentTransferBehavior
                              }
                            }
                          }));
                        }}
                      >
                        <RadioField>
                          <Radio
                            disabled={!hasAdvancedCustomization}
                            color="sky"
                            value={VoiceAgentTransferBehavior.Immediate}
                          />
                          <Label>Immediate Transfer</Label>
                          <Description>Transfer calls immediately when requested.</Description>
                        </RadioField>
                        <RadioField>
                          <Radio
                            disabled={!hasAdvancedCustomization}
                            color="sky"
                            value={VoiceAgentTransferBehavior.AttemptHandling}
                          />
                          <Label>Attempt Handling First</Label>
                          <Description>
                            Try to help the caller first, then transfer if needed.
                          </Description>
                        </RadioField>
                      </RadioGroup>
                      {hasAdvancedCustomization && !voice35Enabled && (
                        <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
                          Turn on <span className="font-medium">Use Voice 3.5</span> above to set
                          transfer timing separately for quotes, claims, COI, EOI, and ID card
                          requests, cancellation, and servicing calls.
                        </p>
                      )}
                      {!!getValidationError(validationErrors, [
                        'configuration',
                        'transfer',
                        'behavior'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, [
                            'configuration',
                            'transfer',
                            'behavior'
                          ])}
                        </ErrorMessage>
                      )}
                      {hasAdvancedCustomization && showPerIntentTransferBehavior && (
                        <div className="mt-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4 dark:border-neutral-700 dark:bg-neutral-900/40">
                          <p className="m-0 text-sm font-medium text-neutral-900 dark:text-white">
                            Transfer behavior by request type
                          </p>
                          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                            For each kind of call, choose whether to transfer right away or try to
                            help the caller first. Each row starts out matching your global setting
                            until you change it.
                          </p>
                          <FieldGroup className="mt-4 gap-y-6">
                            <Field>
                              <Label>Quote and new business</Label>
                              <Description>
                                When someone is asking about a new policy or quote.
                              </Description>
                              <RadioGroup
                                value={
                                  form.configuration?.transfer?.transferBehaviorQuote ||
                                  VoiceAgentTransferBehavior.Immediate
                                }
                                onChange={(value) => {
                                  updateForm(
                                    ['configuration', 'transfer', 'transferBehaviorQuote'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        transfer: {
                                          ...prev.configuration?.transfer,
                                          transferBehaviorQuote: value as VoiceAgentTransferBehavior
                                        }
                                      }
                                    })
                                  );
                                }}
                              >
                                <RadioField>
                                  <Radio color="sky" value={VoiceAgentTransferBehavior.Immediate} />
                                  <Label>Immediate transfer</Label>
                                  <Description>
                                    Transfer as soon as the flow requests it.
                                  </Description>
                                </RadioField>
                                <RadioField>
                                  <Radio
                                    color="sky"
                                    value={VoiceAgentTransferBehavior.AttemptHandling}
                                  />
                                  <Label>Attempt handling first</Label>
                                  <Description>
                                    Try to help the caller first, then transfer if needed.
                                  </Description>
                                </RadioField>
                              </RadioGroup>
                            </Field>
                            <Field>
                              <Label>FNOL and claims</Label>
                              <Description>
                                When the call is about reporting or handling a claim.
                              </Description>
                              <RadioGroup
                                value={
                                  form.configuration?.transfer?.transferBehaviorFnol ||
                                  VoiceAgentTransferBehavior.AttemptHandling
                                }
                                onChange={(value) => {
                                  updateForm(
                                    ['configuration', 'transfer', 'transferBehaviorFnol'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        transfer: {
                                          ...prev.configuration?.transfer,
                                          transferBehaviorFnol: value as VoiceAgentTransferBehavior
                                        }
                                      }
                                    })
                                  );
                                }}
                              >
                                <RadioField>
                                  <Radio color="sky" value={VoiceAgentTransferBehavior.Immediate} />
                                  <Label>Immediate transfer</Label>
                                  <Description>
                                    Transfer as soon as the flow requests it.
                                  </Description>
                                </RadioField>
                                <RadioField>
                                  <Radio
                                    color="sky"
                                    value={VoiceAgentTransferBehavior.AttemptHandling}
                                  />
                                  <Label>Attempt handling first</Label>
                                  <Description>
                                    Try to help the caller first, then transfer if needed.
                                  </Description>
                                </RadioField>
                              </RadioGroup>
                            </Field>
                            <Field>
                              <Label>COI, EOI, ID cards</Label>
                              <Description>
                                When the caller needs a certificate, evidence of insurance, or ID
                                card.
                              </Description>
                              <RadioGroup
                                value={
                                  form.configuration?.transfer?.transferBehaviorCoi ||
                                  VoiceAgentTransferBehavior.AttemptHandling
                                }
                                onChange={(value) => {
                                  updateForm(
                                    ['configuration', 'transfer', 'transferBehaviorCoi'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        transfer: {
                                          ...prev.configuration?.transfer,
                                          transferBehaviorCoi: value as VoiceAgentTransferBehavior
                                        }
                                      }
                                    })
                                  );
                                }}
                              >
                                <RadioField>
                                  <Radio color="sky" value={VoiceAgentTransferBehavior.Immediate} />
                                  <Label>Immediate transfer</Label>
                                  <Description>
                                    Transfer as soon as the flow requests it.
                                  </Description>
                                </RadioField>
                                <RadioField>
                                  <Radio
                                    color="sky"
                                    value={VoiceAgentTransferBehavior.AttemptHandling}
                                  />
                                  <Label>Attempt handling first</Label>
                                  <Description>
                                    Try to help the caller first, then transfer if needed.
                                  </Description>
                                </RadioField>
                              </RadioGroup>
                            </Field>
                            <Field>
                              <Label>Servicing</Label>
                              <Description>Policy questions and endorsement requests.</Description>
                              <RadioGroup
                                value={
                                  form.configuration?.transfer?.transferBehaviorService ||
                                  VoiceAgentTransferBehavior.AttemptHandling
                                }
                                onChange={(value) => {
                                  updateForm(
                                    ['configuration', 'transfer', 'transferBehaviorService'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        transfer: {
                                          ...prev.configuration?.transfer,
                                          transferBehaviorService:
                                            value as VoiceAgentTransferBehavior
                                        }
                                      }
                                    })
                                  );
                                }}
                              >
                                <RadioField>
                                  <Radio color="sky" value={VoiceAgentTransferBehavior.Immediate} />
                                  <Label>Immediate transfer</Label>
                                  <Description>
                                    Transfer as soon as the flow requests it.
                                  </Description>
                                </RadioField>
                                <RadioField>
                                  <Radio
                                    color="sky"
                                    value={VoiceAgentTransferBehavior.AttemptHandling}
                                  />
                                  <Label>Attempt handling first</Label>
                                  <Description>
                                    Try to help the caller first, then transfer if needed.
                                  </Description>
                                </RadioField>
                              </RadioGroup>
                            </Field>
                            <Field>
                              <Label>Cancellation / lost policy release</Label>
                              <Description>
                                When the caller wants to cancel a policy or request a lost policy
                                release.
                              </Description>
                              <RadioGroup
                                value={
                                  form.configuration?.transfer?.transferBehaviorCancellation ||
                                  VoiceAgentTransferBehavior.AttemptHandling
                                }
                                onChange={(value) => {
                                  updateForm(
                                    ['configuration', 'transfer', 'transferBehaviorCancellation'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        transfer: {
                                          ...prev.configuration?.transfer,
                                          transferBehaviorCancellation:
                                            value as VoiceAgentTransferBehavior
                                        }
                                      }
                                    })
                                  );
                                }}
                              >
                                <RadioField>
                                  <Radio color="sky" value={VoiceAgentTransferBehavior.Immediate} />
                                  <Label>Immediate transfer</Label>
                                  <Description>
                                    Transfer as soon as the flow requests it.
                                  </Description>
                                </RadioField>
                                <RadioField>
                                  <Radio
                                    color="sky"
                                    value={VoiceAgentTransferBehavior.AttemptHandling}
                                  />
                                  <Label>Attempt handling first</Label>
                                  <Description>
                                    Try to help the caller first, then transfer if needed.
                                  </Description>
                                </RadioField>
                              </RadioGroup>
                            </Field>
                          </FieldGroup>
                        </div>
                      )}
                    </Field>

                    <SwitchField>
                      <Switch
                        color="sky"
                        disabled={!hasAdvancedCustomization}
                        checked={
                          hasAdvancedCustomization
                            ? form.configuration?.transfer?.collectPurpose || false
                            : false
                        }
                        onChange={(collectPurpose) => {
                          updateForm(['configuration', 'transfer', 'collectPurpose'], (prev) => ({
                            ...prev,
                            configuration: {
                              ...prev.configuration,
                              transfer: { ...prev.configuration?.transfer, collectPurpose }
                            }
                          }));
                        }}
                      />
                      <Label>
                        Collect Call Purpose
                        {!hasAdvancedCustomization && <ProLabel />}
                      </Label>
                      <Description>
                        Require the caller disclose the purpose of their call before Cara transfers
                        the call
                      </Description>
                    </SwitchField>

                    {!voice35Enabled && (
                      <SwitchField>
                        <Switch
                          color="sky"
                          disabled={!hasAdvancedCustomization}
                          checked={
                            hasAdvancedCustomization
                              ? form.configuration?.transfer?.warmTransfer || false
                              : false
                          }
                          onChange={(warmTransfer) => {
                            updateForm(['configuration', 'transfer'], (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                transfer: {
                                  ...prev.configuration?.transfer,
                                  warmTransfer,
                                  transferType: warmTransfer
                                    ? VoiceAgentTransferType.Warm
                                    : VoiceAgentTransferType.Cold
                                }
                              }
                            }));
                          }}
                        />
                        <Label>
                          Warm Transfer
                          {!hasAdvancedCustomization && <ProLabel />}
                        </Label>
                        <Description>
                          Introduce the caller and purpose to the human agent when transferring.
                        </Description>
                      </SwitchField>
                    )}

                    {voice35Enabled && (
                      <Field
                        data-field-id={getFieldId(['configuration', 'transfer', 'transferType'])}
                      >
                        <Label>
                          Transfer type
                          {!hasAdvancedCustomization && <ProLabel />}
                        </Label>
                        <Description>
                          How Cara connects callers to a human agent. Cold is a direct transfer;
                          Warm introduces the caller on connect; Agentic has a back-and-forth with
                          Cara before connecting.
                        </Description>
                        <RadioGroup
                          disabled={!hasAdvancedCustomization}
                          value={
                            hasAdvancedCustomization ? transferType : VoiceAgentTransferType.Cold
                          }
                          onChange={(value) => {
                            const nextType = value as VoiceAgentTransferType;
                            updateForm(['configuration', 'transfer'], (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                transfer: {
                                  ...prev.configuration?.transfer,
                                  transferType: nextType,
                                  warmTransfer: nextType === VoiceAgentTransferType.Warm,
                                  ...(nextType === VoiceAgentTransferType.Warm
                                    ? { sipTransferMode: VoiceAgentSipTransferMode.Refer }
                                    : {})
                                }
                              }
                            }));
                          }}
                        >
                          <RadioField>
                            <Radio
                              disabled={!hasAdvancedCustomization}
                              color="sky"
                              value={VoiceAgentTransferType.Cold}
                            />
                            <Label>Cold transfer</Label>
                            <Description>
                              Transfer the caller directly without pre-connect dialogue.
                            </Description>
                          </RadioField>
                          <RadioField>
                            <Radio
                              disabled={!hasAdvancedCustomization}
                              color="sky"
                              value={VoiceAgentTransferType.Warm}
                            />
                            <Label>Warm transfer</Label>
                            <Description>
                              Cara introduces the caller and purpose to the human agent when
                              connecting.
                            </Description>
                          </RadioField>
                          <RadioField>
                            <Radio
                              disabled={!hasAdvancedCustomization}
                              color="sky"
                              value={VoiceAgentTransferType.Agentic}
                            />
                            <Label>Agentic transfer</Label>
                            <Description>
                              Cara has dialogue with the human agent before connecting with the
                              caller. The human agent can reject the call or ask for more info.
                            </Description>
                          </RadioField>
                        </RadioGroup>
                      </Field>
                    )}

                    {voice35Enabled && (
                      <Field
                        data-field-id={getFieldId(['configuration', 'transfer', 'transferMessage'])}
                      >
                        <Label>
                          Transfer announcement
                          {!hasAdvancedCustomization && <ProLabel />}
                        </Label>
                        <Description>
                          What Cara says before transferring the call. Leave blank for the default
                          message.
                        </Description>
                        <Textarea
                          disabled={!hasAdvancedCustomization}
                          value={form.configuration?.transfer?.transferMessage ?? ''}
                          onChange={(e) => {
                            const transferMessage = e.currentTarget.value;
                            updateForm(
                              ['configuration', 'transfer', 'transferMessage'],
                              (prev) => ({
                                ...prev,
                                configuration: {
                                  ...prev.configuration,
                                  transfer: {
                                    ...prev.configuration?.transfer,
                                    transferMessage
                                  }
                                }
                              })
                            );
                          }}
                          placeholder={DEFAULT_TRANSFER_MESSAGE}
                          style={{ minHeight: '80px' }}
                        />
                      </Field>
                    )}

                    {voice35Enabled && transferType === VoiceAgentTransferType.Cold && (
                      <Field
                        data-field-id={getFieldId(['configuration', 'transfer', 'sipTransferMode'])}
                        className="max-w-md"
                      >
                        <Label>
                          SIP Transfer Mode (cold transfer)
                          {!hasAdvancedCustomization && <ProLabel />}
                        </Label>
                        <Description>
                          Cold transfers: INVITE is the default; choose REFER if your carrier
                          requires SIP REFER.
                        </Description>
                        <Select
                          disabled={!hasAdvancedCustomization}
                          value={
                            form.configuration?.transfer?.sipTransferMode ||
                            VoiceAgentSipTransferMode.Invite
                          }
                          onChange={(e) => {
                            const sipTransferMode = e.currentTarget
                              .value as VoiceAgentSipTransferMode;
                            updateForm(
                              ['configuration', 'transfer', 'sipTransferMode'],
                              (prev) => ({
                                ...prev,
                                configuration: {
                                  ...prev.configuration,
                                  transfer: {
                                    ...prev.configuration?.transfer,
                                    sipTransferMode
                                  }
                                }
                              })
                            );
                          }}
                        >
                          <option value={VoiceAgentSipTransferMode.Invite}>INVITE (default)</option>
                          <option value={VoiceAgentSipTransferMode.Refer}>REFER</option>
                        </Select>
                      </Field>
                    )}

                    <VoiceDiscreteSlider
                      data-field-id={getFieldId(['configuration', 'transfer', 'ringDurationMs'])}
                      className="max-w-md"
                      label="Ring Duration"
                      description="How long to ring before hanging up (5-30 seconds)."
                      options={ringDurationOptions}
                      value={form.configuration?.transfer?.ringDurationMs || 15000}
                      onChange={(ringDurationMs) => {
                        updateForm(['configuration', 'transfer', 'ringDurationMs'], (prev) => ({
                          ...prev,
                          configuration: {
                            ...prev.configuration,
                            transfer: {
                              ...prev.configuration?.transfer,
                              ringDurationMs
                            }
                          }
                        }));
                      }}
                      error={
                        getValidationError(validationErrors, [
                          'configuration',
                          'transfer',
                          'ringDurationMs'
                        ]) || undefined
                      }
                    />

                    <Field
                      data-field-id={getFieldId(['configuration', 'transfer', 'hours', 'timezone'])}
                      className="max-w-md"
                    >
                      <Label>Transfer Hours Timezone</Label>
                      <Description>
                        Timezone for calculating transfer availability hours.
                      </Description>
                      <Select
                        value={form.configuration?.transfer?.hours?.timezone || ''}
                        onChange={(e) => {
                          const timezone = e.currentTarget.value;
                          updateForm(
                            ['configuration', 'transfer', 'hours', 'timezone'],
                            (prev) => ({
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                transfer: {
                                  ...prev.configuration?.transfer,
                                  hours: { ...prev.configuration?.transfer?.hours, timezone }
                                }
                              }
                            })
                          );
                        }}
                        invalid={
                          !!getValidationError(validationErrors, [
                            'configuration',
                            'transfer',
                            'hours',
                            'timezone'
                          ])
                        }
                      >
                        <option value="">Select a timezone...</option>
                        <option value="America/Los_Angeles">Pacific (PT)</option>
                        <option value="America/Denver">Mountain (MT)</option>
                        <option value="America/Chicago">Central (CT)</option>
                        <option value="America/New_York">Eastern (ET)</option>
                      </Select>
                      {!!getValidationError(validationErrors, [
                        'configuration',
                        'transfer',
                        'hours',
                        'timezone'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, [
                            'configuration',
                            'transfer',
                            'hours',
                            'timezone'
                          ])}
                        </ErrorMessage>
                      )}
                    </Field>

                    {/* Daily Transfer Hours Configuration */}
                    <Field data-field-id={getFieldId(['configuration', 'transfer', 'hours'])}>
                      <Label>Daily Transfer Hours</Label>
                      <Description>
                        Configure when transfers are available for each day of the week.
                      </Description>
                      <div className="space-y-3" data-slot="control">
                        {[
                          { day: 'monday', label: 'Monday', shortLabel: 'Mon' },
                          { day: 'tuesday', label: 'Tuesday', shortLabel: 'Tue' },
                          { day: 'wednesday', label: 'Wednesday', shortLabel: 'Wed' },
                          { day: 'thursday', label: 'Thursday', shortLabel: 'Thu' },
                          { day: 'friday', label: 'Friday', shortLabel: 'Fri' },
                          { day: 'saturday', label: 'Saturday', shortLabel: 'Sat' },
                          { day: 'sunday', label: 'Sunday', shortLabel: 'Sun' }
                        ].map(({ day, label }) => (
                          <div key={day} className="flex items-center" data-slot="control">
                            <CheckboxField data-slot="">
                              <Checkbox
                                color="sky"
                                checked={
                                  form.configuration?.transfer?.hours?.[day]?.enabled || false
                                }
                                onChange={(enabled) => {
                                  updateForm(
                                    ['configuration', 'transfer', 'hours', day, 'enabled'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        transfer: {
                                          ...prev.configuration?.transfer,
                                          hours: {
                                            ...prev.configuration?.transfer?.hours,
                                            [day]: {
                                              ...prev.configuration?.transfer?.hours?.[day],
                                              enabled
                                            }
                                          }
                                        }
                                      }
                                    })
                                  );
                                }}
                              />
                            </CheckboxField>
                            <div className="w-24">
                              <span className="text-sm font-medium text-neutral-900 dark:text-white">
                                <span className="inline">{label}</span>
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Select
                                value={
                                  form.configuration?.transfer?.hours?.[day]?.startHour !==
                                    undefined &&
                                  form.configuration?.transfer?.hours?.[day]?.startMinute !==
                                    undefined
                                    ? `${form.configuration.transfer.hours[day].startHour.toString().padStart(2, '0')}:${form.configuration.transfer.hours[day].startMinute.toString().padStart(2, '0')}`
                                    : '09:00'
                                }
                                onChange={(e) => {
                                  const [hour, minute] = e.currentTarget.value
                                    .split(':')
                                    .map(Number);
                                  updateForm(
                                    ['configuration', 'transfer', 'hours', day],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        transfer: {
                                          ...prev.configuration?.transfer,
                                          hours: {
                                            ...prev.configuration?.transfer?.hours,
                                            [day]: {
                                              ...prev.configuration?.transfer?.hours?.[day],
                                              startHour: hour,
                                              startMinute: minute
                                            }
                                          }
                                        }
                                      }
                                    })
                                  );
                                }}
                                className="w-28 sm:w-24 text-xs sm:text-sm"
                                disabled={!form.configuration?.transfer?.hours?.[day]?.enabled}
                              >
                                {Array.from({ length: 24 }, (_, hour) =>
                                  [0, 15, 30, 45, 59]
                                    .filter((minute) => minute < 59 || hour === 23)
                                    .map((minute) => (
                                      <option
                                        key={`${hour}:${minute}`}
                                        value={`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`}
                                      >
                                        {hour.toString().padStart(2, '0')}:
                                        {minute.toString().padStart(2, '0')}
                                      </option>
                                    ))
                                ).flat()}
                              </Select>
                              <span className="text-neutral-500 dark:text-neutral-400 text-xs sm:text-sm">
                                to
                              </span>
                              <Select
                                value={
                                  form.configuration?.transfer?.hours?.[day]?.endHour !==
                                    undefined &&
                                  form.configuration?.transfer?.hours?.[day]?.endMinute !==
                                    undefined
                                    ? `${form.configuration.transfer.hours[day].endHour.toString().padStart(2, '0')}:${form.configuration.transfer.hours[day].endMinute.toString().padStart(2, '0')}`
                                    : '17:00'
                                }
                                onChange={(e) => {
                                  const [hour, minute] = e.currentTarget.value
                                    .split(':')
                                    .map(Number);
                                  updateForm(
                                    ['configuration', 'transfer', 'hours', day],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        transfer: {
                                          ...prev.configuration?.transfer,
                                          hours: {
                                            ...prev.configuration?.transfer?.hours,
                                            [day]: {
                                              ...prev.configuration?.transfer?.hours?.[day],
                                              endHour: hour,
                                              endMinute: minute
                                            }
                                          }
                                        }
                                      }
                                    })
                                  );
                                }}
                                className="w-28 sm:w-24 text-xs sm:text-sm"
                                disabled={!form.configuration?.transfer?.hours?.[day]?.enabled}
                              >
                                {Array.from({ length: 24 }, (_, hour) =>
                                  [0, 15, 30, 45, 59]
                                    .filter((minute) => minute < 59 || hour === 23)
                                    .map((minute) => (
                                      <option
                                        key={`${hour}:${minute}`}
                                        value={`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`}
                                      >
                                        {hour.toString().padStart(2, '0')}:
                                        {minute.toString().padStart(2, '0')}
                                      </option>
                                    ))
                                ).flat()}
                              </Select>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Field>

                    {/* Transfer Directories */}
                    {(() => {
                      const directory = form.configuration?.transfer?.directory ?? [];
                      const lineOfBusinessCatalog = voice35Enabled
                        ? normalizeTransferLineOfBusinessCatalog(
                            form.configuration?.transfer?.lineOfBusinessCatalog
                          )
                        : (form.configuration?.transfer?.lineOfBusinessCatalog ?? []);
                      const directoryTransferType = effectiveVoiceAgentTransferType(
                        form.configuration?.transfer
                      );
                      const updateDirectory = (
                        newDirectory: VoiceAgentSettingsTransferDirectoryEntryInput[]
                      ) =>
                        updateForm(['configuration', 'transfer', 'directory'], (prev) => ({
                          ...prev,
                          configuration: {
                            ...prev.configuration,
                            callBehavior: {
                              ...prev.configuration.callBehavior,
                              requiredContactInfo: ensureLineOfBusinessRequiredContactInfo(
                                prev.configuration.callBehavior.requiredContactInfo,
                                newDirectory,
                                voice35Enabled
                              )
                            },
                            transfer: { ...prev.configuration.transfer, directory: newDirectory }
                          }
                        }));
                      const addLineOfBusiness = ({
                        label,
                        description,
                        examples
                      }: Pick<
                        VoiceAgentTransferLineOfBusinessInput,
                        'label' | 'description' | 'examples'
                      >) => {
                        const lineOfBusiness: VoiceAgentTransferLineOfBusinessInput = {
                          id: `custom-${crypto.randomUUID()}`,
                          label,
                          description,
                          examples
                        };
                        updateForm(
                          ['configuration', 'transfer', 'lineOfBusinessCatalog'],
                          (prev) => ({
                            ...prev,
                            configuration: {
                              ...prev.configuration,
                              transfer: {
                                ...prev.configuration.transfer,
                                lineOfBusinessCatalog: addTransferLineOfBusiness(
                                  prev.configuration.transfer.lineOfBusinessCatalog,
                                  lineOfBusiness
                                )
                              }
                            }
                          })
                        );
                      };
                      const updateLineOfBusiness = (
                        lineOfBusiness: VoiceAgentTransferLineOfBusinessInput
                      ) => {
                        updateForm(
                          ['configuration', 'transfer', 'lineOfBusinessCatalog'],
                          (prev) => ({
                            ...prev,
                            configuration: {
                              ...prev.configuration,
                              transfer: {
                                ...prev.configuration.transfer,
                                lineOfBusinessCatalog: updateTransferLineOfBusiness(
                                  prev.configuration.transfer.lineOfBusinessCatalog,
                                  lineOfBusiness
                                )
                              }
                            }
                          })
                        );
                      };
                      const removeLineOfBusiness = (id: string) => {
                        updateForm(
                          ['configuration', 'transfer', 'lineOfBusinessCatalog'],
                          (prev) => {
                            const updated = removeTransferLineOfBusiness(
                              prev.configuration.transfer.lineOfBusinessCatalog,
                              prev.configuration.transfer.directory,
                              id
                            );
                            return {
                              ...prev,
                              configuration: {
                                ...prev.configuration,
                                callBehavior: {
                                  ...prev.configuration.callBehavior,
                                  requiredContactInfo: ensureLineOfBusinessRequiredContactInfo(
                                    prev.configuration.callBehavior.requiredContactInfo,
                                    updated.directory,
                                    voice35Enabled
                                  )
                                },
                                transfer: {
                                  ...prev.configuration.transfer,
                                  lineOfBusinessCatalog: updated.catalog,
                                  directory: updated.directory
                                }
                              }
                            };
                          }
                        );
                      };
                      const boundGetValidationError = (path: string[]) =>
                        getValidationError(validationErrors, path);
                      return (
                        <>
                          {voice35Enabled && (
                            <VoiceLineOfBusinessCatalogEditor
                              catalog={lineOfBusinessCatalog}
                              builtInIds={DEFAULT_TRANSFER_LINE_OF_BUSINESS_IDS}
                              assignedEntryCountById={countTransferDirectoryEntriesByLineOfBusiness(
                                directory
                              )}
                              fieldId={getFieldId([
                                'configuration',
                                'transfer',
                                'lineOfBusinessCatalog'
                              ])}
                              validationError={
                                getValidationError(validationErrors, [
                                  'configuration',
                                  'transfer',
                                  'lineOfBusinessCatalog'
                                ]) ??
                                validationErrors.find((error) => {
                                  const catalogIndex = error.field.indexOf('lineOfBusinessCatalog');
                                  return (
                                    catalogIndex >= 2 &&
                                    error.field[catalogIndex - 2] === 'configuration' &&
                                    error.field[catalogIndex - 1] === 'transfer'
                                  );
                                })?.message
                              }
                              onAdd={addLineOfBusiness}
                              onUpdate={updateLineOfBusiness}
                              onRemove={removeLineOfBusiness}
                            />
                          )}

                          {voice35Enabled && directoryImportEnabled && (
                            <TransferDirectoryImport
                              importSchema={props.transferDirectoryImportSchema}
                              directory={directory}
                              lineOfBusinessCatalog={lineOfBusinessCatalog}
                              externalTransferEnabled={
                                !!form.configuration?.transfer?.enableExternalTransfer
                              }
                              hasAdvancedCustomization={hasAdvancedCustomization}
                              onDirectoryChange={updateDirectory}
                            />
                          )}

                          <TransferDirectorySection
                            sectionLabel={
                              voice35Enabled ? 'Internal Transfer Directory' : 'Transfer Directory'
                            }
                            entryType={VoiceAgentTransferDirectoryEntryType.Internal}
                            description={
                              useStructuredTransferDirectory ? (
                                <Description>
                                  Configure who in your agency Cara can transfer calls to and when.
                                  Use structured fields (name, phone, call types, CSR code, aliases)
                                  first; those route deterministically. Older rows that only had a
                                  single label copy that text into Name when you enable this flow.
                                </Description>
                              ) : (
                                <Description>
                                  Configure who Cara can transfer calls to and when. Each entry
                                  should specify the name/department, phone number, and criteria for
                                  when to transfer. Extensions are not supported on phone numbers.
                                </Description>
                              )
                            }
                            directory={directory}
                            lineOfBusinessCatalog={lineOfBusinessCatalog}
                            onUpdateDirectory={updateDirectory}
                            hasAdvancedCustomization={hasAdvancedCustomization}
                            useStructured={useStructuredTransferDirectory}
                            voice35Enabled={voice35Enabled}
                            transferType={directoryTransferType}
                            sipTransferMode={
                              form.configuration?.transfer?.sipTransferMode ||
                              VoiceAgentSipTransferMode.Invite
                            }
                            getFieldId={getFieldId}
                            getValidationError={boundGetValidationError}
                            hasRowError={(row) => directoryRowHasError(validationErrors, row)}
                            openEntryIndex={openDirectoryEntryIndex}
                            setOpenEntryIndex={setOpenDirectoryEntryIndex}
                          />

                          {voice35Enabled && (
                            <SwitchField>
                              <Switch
                                color="sky"
                                checked={
                                  form.configuration?.transfer?.enableExternalTransfer ?? false
                                }
                                onChange={(enableExternalTransfer) => {
                                  updateForm(
                                    ['configuration', 'transfer', 'enableExternalTransfer'],
                                    (prev) => ({
                                      ...prev,
                                      configuration: {
                                        ...prev.configuration,
                                        transfer: {
                                          ...prev.configuration?.transfer,
                                          enableExternalTransfer
                                        }
                                      }
                                    })
                                  );
                                }}
                              />
                              <Label>Enable External Transfer</Label>
                              <Description>
                                Allow Cara to transfer callers to external parties such as carrier
                                claim centers, lenders, or specialist vendors.
                              </Description>
                            </SwitchField>
                          )}

                          {voice35Enabled &&
                            form.configuration?.transfer?.enableExternalTransfer && (
                              <>
                                <Field
                                  data-field-id={getFieldId([
                                    'configuration',
                                    'transfer',
                                    'externalTransferBehavior'
                                  ])}
                                >
                                  <Label>External transfer behavior</Label>
                                  <Description>
                                    Controls how Cara routes between internal staff and external
                                    parties (carriers, vendors). Carrier names from lookup_customer
                                    are the primary match signal for external numbers.
                                  </Description>
                                  <div data-slot="control" className="max-w-md">
                                    <Select
                                      disabled={!hasAdvancedCustomization}
                                      value={effectiveExternalTransferBehavior(
                                        form.configuration?.transfer
                                      )}
                                      onChange={(e) => {
                                        const externalTransferBehavior = e.currentTarget
                                          .value as VoiceAgentExternalTransferBehavior;
                                        updateForm(
                                          ['configuration', 'transfer', 'externalTransferBehavior'],
                                          (prev) => ({
                                            ...prev,
                                            configuration: {
                                              ...prev.configuration,
                                              transfer: {
                                                ...prev.configuration?.transfer,
                                                externalTransferBehavior
                                              }
                                            }
                                          })
                                        );
                                      }}
                                      invalid={
                                        !!getValidationError(validationErrors, [
                                          'configuration',
                                          'transfer',
                                          'externalTransferBehavior'
                                        ])
                                      }
                                    >
                                      {EXTERNAL_TRANSFER_BEHAVIOR_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </Select>
                                  </div>
                                  <Description className="mt-2">
                                    {
                                      EXTERNAL_TRANSFER_BEHAVIOR_OPTIONS.find(
                                        (o) =>
                                          o.value ===
                                          effectiveExternalTransferBehavior(
                                            form.configuration?.transfer
                                          )
                                      )?.description
                                    }
                                  </Description>
                                </Field>

                                <TransferDirectorySection
                                  sectionLabel="External Transfer Directory"
                                  entryType={VoiceAgentTransferDirectoryEntryType.External}
                                  description={
                                    <Description>
                                      Configure external parties Cara can transfer callers to. Each
                                      entry is matched by name when the caller clearly asks for that
                                      party (or when Cara learns the name from your AMS). Select at
                                      least one Call Type per entry to enable external transfers for
                                      those types.
                                    </Description>
                                  }
                                  directory={directory}
                                  lineOfBusinessCatalog={lineOfBusinessCatalog}
                                  onUpdateDirectory={updateDirectory}
                                  hasAdvancedCustomization={hasAdvancedCustomization}
                                  useStructured={true}
                                  voice35Enabled={true}
                                  transferType={VoiceAgentTransferType.Cold}
                                  sipTransferMode={
                                    form.configuration?.transfer?.sipTransferMode ||
                                    VoiceAgentSipTransferMode.Invite
                                  }
                                  getFieldId={getFieldId}
                                  getValidationError={boundGetValidationError}
                                  hasRowError={(row) => directoryRowHasError(validationErrors, row)}
                                  openEntryIndex={openDirectoryEntryIndex}
                                  setOpenEntryIndex={setOpenDirectoryEntryIndex}
                                />
                              </>
                            )}
                        </>
                      );
                    })()}
                    <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
                      <div className="mb-4">
                        <h5 className="text-base font-semibold text-neutral-900 dark:text-white m-0 p-0">
                          Fallback Attempts
                          {!hasAdvancedCustomization && <ProLabel />}
                        </h5>
                        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                          Controls retry behavior for all transfers — internal and external.
                        </p>
                      </div>
                      <Field
                        data-field-id={getFieldId([
                          'configuration',
                          'transfer',
                          'fallbackAttempts'
                        ])}
                      >
                        <Description>
                          {voice35Enabled
                            ? 'How many internal numbers Cara may dial on one call if a transfer does not connect. No Fallbacks = one dial; One Fallback Call = up to two dials; Two Fallback Calls = up to three. Each dial rings for the duration set in Ring Duration above. Cara never dials the same number twice. External parties are dialed at most once per call. When external transfer behavior allows internal fallback, this setting also controls internal retries after an unanswered external transfer (requires at least One Fallback Call).'
                            : 'Number of total transfer attempts if the first transfer fails (up to 3 total transfer attempts). Each transfer will ring for the duration set in Ring Duration above. Cara will not try the same number twice.'}
                        </Description>
                        <div
                          data-slot="control"
                          className={voice35Enabled ? 'max-w-md' : undefined}
                          style={voice35Enabled ? undefined : { maxWidth: '80px' }}
                        >
                          {voice35Enabled ? (
                            <Select
                              disabled={!hasAdvancedCustomization}
                              value={String(
                                voice35FallbackAttemptSelectValue(
                                  hasAdvancedCustomization
                                    ? form.configuration?.transfer?.fallbackAttempts
                                    : 0
                                )
                              )}
                              onChange={(e) => {
                                const fallbackAttempts = parseInt(e.currentTarget.value, 10);
                                updateForm(
                                  ['configuration', 'transfer', 'fallbackAttempts'],
                                  (prev) => ({
                                    ...prev,
                                    configuration: {
                                      ...prev.configuration,
                                      transfer: {
                                        ...prev.configuration?.transfer,
                                        fallbackAttempts
                                      }
                                    }
                                  })
                                );
                              }}
                              invalid={
                                !!getValidationError(validationErrors, [
                                  'configuration',
                                  'transfer',
                                  'fallbackAttempts'
                                ])
                              }
                            >
                              {VOICE35_FALLBACK_ATTEMPT_OPTIONS.map((option) => (
                                <option key={option.value} value={String(option.value)}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <Input
                              disabled={!hasAdvancedCustomization}
                              type="number"
                              value={
                                hasAdvancedCustomization
                                  ? form.configuration?.transfer?.fallbackAttempts
                                  : 0
                              }
                              onChange={(e) => {
                                const fallbackAttempts = parseInt(e.currentTarget.value);
                                updateForm(
                                  ['configuration', 'transfer', 'fallbackAttempts'],
                                  (prev) => ({
                                    ...prev,
                                    configuration: {
                                      ...prev.configuration,
                                      transfer: {
                                        ...prev.configuration?.transfer,
                                        fallbackAttempts
                                      }
                                    }
                                  })
                                );
                              }}
                              invalid={
                                !!getValidationError(validationErrors, [
                                  'configuration',
                                  'transfer',
                                  'fallbackAttempts'
                                ])
                              }
                            />
                          )}
                        </div>
                        {!!getValidationError(validationErrors, [
                          'configuration',
                          'transfer',
                          'fallbackAttempts'
                        ]) && (
                          <ErrorMessage>
                            {getValidationError(validationErrors, [
                              'configuration',
                              'transfer',
                              'fallbackAttempts'
                            ])}
                          </ErrorMessage>
                        )}
                      </Field>
                    </div>

                    {/* Transfer Time Off */}
                    <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
                      <div className="mb-4">
                        <h5 className="text-base font-semibold text-neutral-900 dark:text-white m-0 p-0">
                          Transfer Time Off
                        </h5>
                        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                          Configure scheduled periods when transfers will be automatically disabled.
                          You can customize the intro and instructions for each scheduled period.
                        </p>
                      </div>

                      <div className="space-y-4">
                        {getSchedules().map(
                          (
                            schedule: VoiceAgentConfigurationTransferTimeOffScheduleInput,
                            index: number
                          ) => {
                            const scheduleType = schedule.type;
                            const summary = getScheduleSummary(schedule);

                            const isExpanded = expandedSchedules.has(index);

                            return (
                              <Headless.Disclosure key={index} as="div" defaultOpen={isExpanded}>
                                {({ open }) => {
                                  // Update state when disclosure state changes
                                  React.useEffect(() => {
                                    if (open !== isExpanded) {
                                      setExpandedSchedules((prev) => {
                                        const next = new Set(prev);
                                        if (open) {
                                          next.add(index);
                                        } else {
                                          next.delete(index);
                                        }
                                        return next;
                                      });
                                    }
                                  }, [open, isExpanded, index]);

                                  return (
                                    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow-sm">
                                      <Headless.DisclosureButton className="flex w-full items-center justify-between p-4 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-3">
                                            <ChevronDownIcon
                                              className={clsx(
                                                'h-5 w-5 text-neutral-400 dark:text-neutral-500 transition-transform duration-200',
                                                open ? 'rotate-180' : ''
                                              )}
                                            />
                                            <div className="flex-1 min-w-0">
                                              <div className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                                                {schedule.name || `Schedule ${index + 1}`}
                                              </div>
                                              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                                                {summary}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <Button
                                          color="red"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateSchedules((schedules) => {
                                              const updated = [...schedules];
                                              updated.splice(index, 1);
                                              return updated;
                                            });
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      </Headless.DisclosureButton>

                                      <Headless.DisclosurePanel className="px-4 pb-4 space-y-4 border-t border-neutral-200 dark:border-neutral-700 pt-4">
                                        <Field>
                                          <Label>Schedule Name (Optional)</Label>
                                          <Description>
                                            Give this schedule a name for easy identification (e.g.,
                                            "Lunch Break", "Christmas Holiday").
                                          </Description>
                                          <Input
                                            value={schedule.name || ''}
                                            onChange={(e) => {
                                              updateSchedule(index, (schedule) => ({
                                                ...schedule,
                                                name: e.currentTarget.value
                                              }));
                                            }}
                                            placeholder="e.g., Lunch Break, Christmas"
                                          />
                                        </Field>

                                        <Field
                                          data-field-id={getFieldId([
                                            'configuration',
                                            'transfer',
                                            'timeOffSchedules',
                                            index.toString(),
                                            'type'
                                          ])}
                                        >
                                          <Label>Schedule Type</Label>
                                          <Description>
                                            Select the type of schedule: daily breaks for recurring
                                            time periods, or scheduled closures for particular
                                            dates.
                                          </Description>
                                          <Select
                                            value={scheduleType || ''}
                                            invalid={
                                              !!getValidationError(validationErrors, [
                                                'configuration',
                                                'transfer',
                                                'timeOffSchedules',
                                                index.toString(),
                                                'type'
                                              ])
                                            }
                                            onChange={(e) => {
                                              const value = e.currentTarget.value;
                                              const type =
                                                value === ''
                                                  ? undefined
                                                  : (value as VoiceAgentTransferTimeOffScheduleType);
                                              updateSchedule(index, (schedule) => ({
                                                ...schedule,
                                                type,
                                                // Clear opposite type fields
                                                ...(type ===
                                                VoiceAgentTransferTimeOffScheduleType.DailyBreaks
                                                  ? { scheduledClosure: undefined }
                                                  : type ===
                                                      VoiceAgentTransferTimeOffScheduleType.ScheduledClosures
                                                    ? { dailyBreak: undefined }
                                                    : {
                                                        dailyBreak: undefined,
                                                        scheduledClosure: undefined
                                                      })
                                              }));
                                            }}
                                          >
                                            <option value="" disabled>
                                              Select a schedule type...
                                            </option>
                                            <option value="DAILY_BREAKS">Daily Breaks</option>
                                            <option value="SCHEDULED_CLOSURES">
                                              Scheduled Closures
                                            </option>
                                          </Select>
                                          {!!getValidationError(validationErrors, [
                                            'configuration',
                                            'transfer',
                                            'timeOffSchedules',
                                            index.toString(),
                                            'type'
                                          ]) && (
                                            <ErrorMessage>
                                              {getValidationError(validationErrors, [
                                                'configuration',
                                                'transfer',
                                                'timeOffSchedules',
                                                index.toString(),
                                                'type'
                                              ])}
                                            </ErrorMessage>
                                          )}
                                        </Field>

                                        {!scheduleType && (
                                          <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                            Select a schedule type to continue configuring this
                                            entry.
                                          </p>
                                        )}

                                        {scheduleType ===
                                          VoiceAgentTransferTimeOffScheduleType.DailyBreaks && (
                                          <>
                                            {/* Display error for missing dailyBreak configuration */}
                                            {!!getValidationError(validationErrors, [
                                              'configuration',
                                              'transfer',
                                              'timeOffSchedules',
                                              index.toString(),
                                              'dailyBreak'
                                            ]) && (
                                              <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                                                <div className="flex">
                                                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                                                  <div className="ml-3">
                                                    <ErrorMessage>
                                                      {getValidationError(validationErrors, [
                                                        'configuration',
                                                        'transfer',
                                                        'timeOffSchedules',
                                                        index.toString(),
                                                        'dailyBreak'
                                                      ])}
                                                    </ErrorMessage>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            <Field
                                              data-field-id={getFieldId([
                                                'configuration',
                                                'transfer',
                                                'timeOffSchedules',
                                                index.toString(),
                                                'daysOfWeek'
                                              ])}
                                            >
                                              <Label>Days of Week</Label>
                                              <Description>
                                                Select which day(s) of the week this schedule
                                                applies to. You can select multiple days.
                                              </Description>
                                              <div className="space-y-2" data-slot="control">
                                                {[
                                                  { value: DayOfWeek.Sunday, label: 'Sunday' },
                                                  { value: DayOfWeek.Monday, label: 'Monday' },
                                                  { value: DayOfWeek.Tuesday, label: 'Tuesday' },
                                                  {
                                                    value: DayOfWeek.Wednesday,
                                                    label: 'Wednesday'
                                                  },
                                                  { value: DayOfWeek.Thursday, label: 'Thursday' },
                                                  { value: DayOfWeek.Friday, label: 'Friday' },
                                                  { value: DayOfWeek.Saturday, label: 'Saturday' }
                                                ].map(({ value, label }) => {
                                                  const currentDays =
                                                    schedule.dailyBreak?.daysOfWeek || [];
                                                  const isChecked = currentDays.includes(value);
                                                  return (
                                                    <CheckboxField key={value}>
                                                      <Checkbox
                                                        color="sky"
                                                        checked={isChecked}
                                                        onChange={(checked) => {
                                                          updateSchedule(index, (schedule) => {
                                                            const currentDays =
                                                              schedule.dailyBreak?.daysOfWeek || [];
                                                            let newDays: DayOfWeek[];
                                                            if (checked) {
                                                              newDays = [...currentDays, value];
                                                            } else {
                                                              newDays = currentDays.filter(
                                                                (d) => d !== value
                                                              );
                                                            }
                                                            return {
                                                              ...schedule,
                                                              dailyBreak: {
                                                                ...schedule.dailyBreak,
                                                                daysOfWeek: newDays,
                                                                startHour:
                                                                  schedule.dailyBreak?.startHour ??
                                                                  0,
                                                                startMinute:
                                                                  schedule.dailyBreak
                                                                    ?.startMinute ?? 0,
                                                                endHour:
                                                                  schedule.dailyBreak?.endHour ?? 0,
                                                                endMinute:
                                                                  schedule.dailyBreak?.endMinute ??
                                                                  0
                                                              }
                                                            };
                                                          });
                                                        }}
                                                      />
                                                      <Label>{label}</Label>
                                                    </CheckboxField>
                                                  );
                                                })}
                                              </div>
                                              {!!getValidationError(validationErrors, [
                                                'configuration',
                                                'transfer',
                                                'timeOffSchedules',
                                                index.toString(),
                                                'daysOfWeek'
                                              ]) && (
                                                <ErrorMessage>
                                                  {getValidationError(validationErrors, [
                                                    'configuration',
                                                    'transfer',
                                                    'timeOffSchedules',
                                                    index.toString(),
                                                    'daysOfWeek'
                                                  ])}
                                                </ErrorMessage>
                                              )}
                                            </Field>

                                            <div className="grid grid-cols-2 gap-4">
                                              <Field
                                                data-field-id={getFieldId([
                                                  'configuration',
                                                  'transfer',
                                                  'timeOffSchedules',
                                                  index.toString(),
                                                  'startHour'
                                                ])}
                                              >
                                                <Label>Start Time</Label>
                                                <Select
                                                  value={hourMinuteToTimeString(
                                                    schedule.dailyBreak?.startHour,
                                                    schedule.dailyBreak?.startMinute
                                                  )}
                                                  invalid={
                                                    !!getValidationError(validationErrors, [
                                                      'configuration',
                                                      'transfer',
                                                      'timeOffSchedules',
                                                      index.toString(),
                                                      'dailyBreak',
                                                      'startHour'
                                                    ]) ||
                                                    !!getValidationError(validationErrors, [
                                                      'configuration',
                                                      'transfer',
                                                      'timeOffSchedules',
                                                      index.toString(),
                                                      'dailyBreak',
                                                      'startMinute'
                                                    ])
                                                  }
                                                  onChange={(e) => {
                                                    const timeStr = e.currentTarget.value;
                                                    const timeParts =
                                                      timeStringToHourMinute(timeStr);
                                                    updateSchedule(index, (schedule) => ({
                                                      ...schedule,
                                                      dailyBreak: {
                                                        ...schedule.dailyBreak,
                                                        daysOfWeek:
                                                          schedule.dailyBreak?.daysOfWeek || [],
                                                        startHour: timeParts?.hour ?? 0,
                                                        startMinute: timeParts?.minute ?? 0,
                                                        endHour: schedule.dailyBreak?.endHour ?? 0,
                                                        endMinute:
                                                          schedule.dailyBreak?.endMinute ?? 0
                                                      }
                                                    }));
                                                  }}
                                                >
                                                  <option value="">Select start time...</option>
                                                  {Array.from({ length: 24 }, (_, hour) =>
                                                    [0, 15, 30, 45].map((minute) => (
                                                      <option
                                                        key={`${hour}:${minute}`}
                                                        value={`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`}
                                                      >
                                                        {hour.toString().padStart(2, '0')}:
                                                        {minute.toString().padStart(2, '0')}
                                                      </option>
                                                    ))
                                                  ).flat()}
                                                </Select>
                                                {(!!getValidationError(validationErrors, [
                                                  'configuration',
                                                  'transfer',
                                                  'timeOffSchedules',
                                                  index.toString(),
                                                  'dailyBreak',
                                                  'startHour'
                                                ]) ||
                                                  !!getValidationError(validationErrors, [
                                                    'configuration',
                                                    'transfer',
                                                    'timeOffSchedules',
                                                    index.toString(),
                                                    'dailyBreak',
                                                    'startMinute'
                                                  ])) && (
                                                  <ErrorMessage>
                                                    {getValidationError(validationErrors, [
                                                      'configuration',
                                                      'transfer',
                                                      'timeOffSchedules',
                                                      index.toString(),
                                                      'dailyBreak',
                                                      'startHour'
                                                    ]) ||
                                                      getValidationError(validationErrors, [
                                                        'configuration',
                                                        'transfer',
                                                        'timeOffSchedules',
                                                        index.toString(),
                                                        'dailyBreak',
                                                        'startMinute'
                                                      ])}
                                                  </ErrorMessage>
                                                )}
                                              </Field>

                                              <Field
                                                data-field-id={getFieldId([
                                                  'configuration',
                                                  'transfer',
                                                  'timeOffSchedules',
                                                  index.toString(),
                                                  'endHour'
                                                ])}
                                              >
                                                <Label>End Time</Label>
                                                <Select
                                                  value={hourMinuteToTimeString(
                                                    schedule.dailyBreak?.endHour,
                                                    schedule.dailyBreak?.endMinute
                                                  )}
                                                  invalid={
                                                    !!getValidationError(validationErrors, [
                                                      'configuration',
                                                      'transfer',
                                                      'timeOffSchedules',
                                                      index.toString(),
                                                      'dailyBreak',
                                                      'endHour'
                                                    ]) ||
                                                    !!getValidationError(validationErrors, [
                                                      'configuration',
                                                      'transfer',
                                                      'timeOffSchedules',
                                                      index.toString(),
                                                      'dailyBreak',
                                                      'endMinute'
                                                    ])
                                                  }
                                                  onChange={(e) => {
                                                    const timeStr = e.currentTarget.value;
                                                    const timeParts =
                                                      timeStringToHourMinute(timeStr);
                                                    updateSchedule(index, (schedule) => ({
                                                      ...schedule,
                                                      dailyBreak: {
                                                        ...schedule.dailyBreak,
                                                        daysOfWeek:
                                                          schedule.dailyBreak?.daysOfWeek || [],
                                                        startHour:
                                                          schedule.dailyBreak?.startHour ?? 0,
                                                        startMinute:
                                                          schedule.dailyBreak?.startMinute ?? 0,
                                                        endHour: timeParts?.hour ?? 0,
                                                        endMinute: timeParts?.minute ?? 0
                                                      }
                                                    }));
                                                  }}
                                                >
                                                  <option value="">Select end time...</option>
                                                  {Array.from({ length: 24 }, (_, hour) =>
                                                    [0, 15, 30, 45, 59]
                                                      .filter(
                                                        (minute) => minute < 59 || hour === 23
                                                      )
                                                      .map((minute) => (
                                                        <option
                                                          key={`${hour}:${minute}`}
                                                          value={`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`}
                                                        >
                                                          {hour.toString().padStart(2, '0')}:
                                                          {minute.toString().padStart(2, '0')}
                                                        </option>
                                                      ))
                                                  ).flat()}
                                                </Select>
                                                {(!!getValidationError(validationErrors, [
                                                  'configuration',
                                                  'transfer',
                                                  'timeOffSchedules',
                                                  index.toString(),
                                                  'dailyBreak',
                                                  'endHour'
                                                ]) ||
                                                  !!getValidationError(validationErrors, [
                                                    'configuration',
                                                    'transfer',
                                                    'timeOffSchedules',
                                                    index.toString(),
                                                    'dailyBreak',
                                                    'endMinute'
                                                  ])) && (
                                                  <ErrorMessage>
                                                    {getValidationError(validationErrors, [
                                                      'configuration',
                                                      'transfer',
                                                      'timeOffSchedules',
                                                      index.toString(),
                                                      'dailyBreak',
                                                      'endHour'
                                                    ]) ||
                                                      getValidationError(validationErrors, [
                                                        'configuration',
                                                        'transfer',
                                                        'timeOffSchedules',
                                                        index.toString(),
                                                        'dailyBreak',
                                                        'endMinute'
                                                      ])}
                                                  </ErrorMessage>
                                                )}
                                              </Field>
                                            </div>
                                          </>
                                        )}

                                        {scheduleType ===
                                          VoiceAgentTransferTimeOffScheduleType.ScheduledClosures && (
                                          <>
                                            {/* Display error for missing scheduledClosure configuration */}
                                            {!!getValidationError(validationErrors, [
                                              'configuration',
                                              'transfer',
                                              'timeOffSchedules',
                                              index.toString(),
                                              'scheduledClosure'
                                            ]) && (
                                              <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                                                <div className="flex">
                                                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                                                  <div className="ml-3">
                                                    <ErrorMessage>
                                                      {getValidationError(validationErrors, [
                                                        'configuration',
                                                        'transfer',
                                                        'timeOffSchedules',
                                                        index.toString(),
                                                        'scheduledClosure'
                                                      ])}
                                                    </ErrorMessage>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            <Field
                                              data-field-id={getFieldId([
                                                'configuration',
                                                'transfer',
                                                'timeOffSchedules',
                                                index.toString(),
                                                'date'
                                              ])}
                                            >
                                              <Label>Start Date</Label>
                                              <Description>
                                                The date when transfers should be turned off.
                                              </Description>
                                              <Input
                                                type="date"
                                                value={schedule.scheduledClosure?.date || ''}
                                                invalid={
                                                  !!getValidationError(validationErrors, [
                                                    'configuration',
                                                    'transfer',
                                                    'timeOffSchedules',
                                                    index.toString(),
                                                    'scheduledClosure',
                                                    'date'
                                                  ])
                                                }
                                                onChange={(e) => {
                                                  const date = e.currentTarget.value;
                                                  updateSchedule(index, (schedule) => ({
                                                    ...schedule,
                                                    scheduledClosure: {
                                                      ...schedule.scheduledClosure,
                                                      date,
                                                      endDate: schedule.scheduledClosure?.endDate
                                                    }
                                                  }));
                                                }}
                                              />
                                              {!!getValidationError(validationErrors, [
                                                'configuration',
                                                'transfer',
                                                'timeOffSchedules',
                                                index.toString(),
                                                'date'
                                              ]) && (
                                                <ErrorMessage>
                                                  {getValidationError(validationErrors, [
                                                    'configuration',
                                                    'transfer',
                                                    'timeOffSchedules',
                                                    index.toString(),
                                                    'date'
                                                  ])}
                                                </ErrorMessage>
                                              )}
                                            </Field>

                                            <Field
                                              data-field-id={getFieldId([
                                                'configuration',
                                                'transfer',
                                                'timeOffSchedules',
                                                index.toString(),
                                                'scheduledClosure',
                                                'endDate'
                                              ])}
                                            >
                                              <Label>End Date (Optional)</Label>
                                              <Description>
                                                Leave blank for a single day, or set an end date for
                                                a date range.
                                              </Description>
                                              <Input
                                                type="date"
                                                value={schedule.scheduledClosure?.endDate || ''}
                                                invalid={
                                                  !!getValidationError(validationErrors, [
                                                    'configuration',
                                                    'transfer',
                                                    'timeOffSchedules',
                                                    index.toString(),
                                                    'scheduledClosure',
                                                    'endDate'
                                                  ])
                                                }
                                                onChange={(e) => {
                                                  const endDate = e.currentTarget.value;
                                                  updateSchedule(index, (schedule) => ({
                                                    ...schedule,
                                                    scheduledClosure: {
                                                      ...schedule.scheduledClosure,
                                                      date: schedule.scheduledClosure?.date || '',
                                                      endDate: endDate || undefined
                                                    }
                                                  }));
                                                }}
                                              />
                                              {!!getValidationError(validationErrors, [
                                                'configuration',
                                                'transfer',
                                                'timeOffSchedules',
                                                index.toString(),
                                                'scheduledClosure',
                                                'endDate'
                                              ]) && (
                                                <ErrorMessage>
                                                  {getValidationError(validationErrors, [
                                                    'configuration',
                                                    'transfer',
                                                    'timeOffSchedules',
                                                    index.toString(),
                                                    'scheduledClosure',
                                                    'endDate'
                                                  ])}
                                                </ErrorMessage>
                                              )}
                                            </Field>
                                          </>
                                        )}

                                        <Field>
                                          <Label>Custom Intro (Optional)</Label>
                                          <Description>
                                            Replace the current intro with a custom intro for this
                                            scheduled time off period. If blank, the default intro
                                            will be used.
                                          </Description>
                                          <Textarea
                                            value={schedule.customIntro || ''}
                                            onChange={(e) => {
                                              const customIntro = e.currentTarget.value;
                                              updateSchedule(index, (schedule) => ({
                                                ...schedule,
                                                customIntro
                                              }));
                                            }}
                                            placeholder="Hello! This is Cara. We're currently on break, but I can still help you with..."
                                            style={{ minHeight: '80px' }}
                                          />
                                        </Field>

                                        <Field>
                                          <Label>Additional Instructions (Optional)</Label>
                                          <Description>
                                            These instructions will be added to your Global Custom
                                            Instructions when transfers are scheduled to be off
                                            during this time period.
                                          </Description>
                                          <Textarea
                                            value={schedule.additionalInstructions || ''}
                                            onChange={(e) => {
                                              const additionalInstructions = e.currentTarget.value;
                                              updateSchedule(index, (schedule) => ({
                                                ...schedule,
                                                additionalInstructions
                                              }));
                                            }}
                                            placeholder="Inform callers that transfers are currently unavailable during this time. If they need immediate assistance, suggest they call back after..."
                                            style={{ minHeight: '80px' }}
                                          />
                                        </Field>
                                      </Headless.DisclosurePanel>
                                    </div>
                                  );
                                }}
                              </Headless.Disclosure>
                            );
                          }
                        )}

                        <Button
                          color="sky"
                          onClick={() => {
                            const newSchedule: VoiceAgentConfigurationTransferTimeOffScheduleInput =
                              {
                                id: `schedule-${Date.now()}`,
                                type: undefined,
                                dailyBreak: undefined,
                                scheduledClosure: undefined,
                                customIntro: undefined,
                                additionalInstructions: undefined,
                                name: undefined
                              };
                            updateSchedules((schedules) => {
                              const newSchedules = [...schedules, newSchedule];
                              // Expand the newly added schedule (it will be at the last index)
                              setExpandedSchedules((prev) =>
                                new Set(prev).add(newSchedules.length - 1)
                              );
                              return newSchedules;
                            });
                          }}
                        >
                          Add Transfer Time Off
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </FieldGroup>
            </Fieldset>
          </div>
        </div>
      </div>
    </Form>
  );
};
